import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { NgSelectComponent } from '@ng-select/ng-select';
import { commonImages } from '@images/common-images';
import { leadImages } from '@images/lead-images';
import { ApiService } from '@services/api/api.service';
import { AlertService } from '@services/helpers/alert.service';
import { LeadsService } from '@services/api/leads.service';
import { ListingPropertiesService } from '@services/api/listing-properties.service';
import { SettingService } from '@services/api/setting.service';
import { SharedService } from '@services/api/shared.service';
import { TagMangerService } from '@services/api/tag-manger.service';
import { TaskService } from '@services/api/task.service';
import { UserService } from '@services/api/user.service';
import { WorkflowService } from '@services/api/workflow.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { FormHelper } from '@services/helpers/form-helper';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { Utils } from '@services/helpers/utils';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ActivityTrackerService } from '@services/user-activity/activity-tracker.service';
import { MessageService } from '@services/util/message.service';
import { ToasterService } from '@services/util/toaster.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { DUPLICATE_LEAD_STATUS, MergeResult, SkipResult } from '../duplicate-lead-merge';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { DashService } from '../../dash-board/dash.service';
import { Leads } from '../leads/leads';
import { FollowUpService } from './followUp.service';
import { FollowUpSelection } from '@models/follow-up-options.model';
import { FollowUpOptionsService } from '@services/api/follow-up-options.service';
import { ProjectService } from '@services/api/project.service';
import { timer } from 'rxjs';
import { LoadingService } from '@common/loading/loading.service';
import { CalendarAPIService } from '@services/api/calendarAPI.service';
import { LeadSmsService } from '@services/api/lead-sms.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LeadReportNotesSummaryComponent } from './lead-report-notes-summary/lead-report-notes-summary.component';
import { CollapseBoxComponent } from '@common/collapse-box/collapse-box.component';
import { DuplicateContactDraft } from '@modules/contact/possible-duplicate-contact/possible-duplicate-contact.component';
import { normalisePostcode } from '@services/helpers/postcode.util';
import { ContactRequirement, NoResponseFollowUpChoice } from './no-response-blocked-dialog/no-response-blocked-dialog.component';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

/** The lead status picker, its log and the activity timeline, as they stood before a save. */
interface LeadStatusState {
  status: string;
  statusType: string;
  log: any[] | null;
  timeline: any[];
}

@Component({
  selector: 'app-edit-lead-detail',
  templateUrl: './edit-lead-detail.component.html',
  styleUrls: ['./edit-lead-detail.component.scss'],
})
export class EditLeadDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  Utils = Utils; // 👈 expose Utils class to the template
  @Input() entityid: any;
  @Input() taskUpdate: Observable<any> = null;
  @Input() eoiUpdate: Observable<any> = null;
  private _lead_detail: any = null;
  /**
   * The project name, captured the moment `lead_detail` ever carries one (see the setter below).
   *
   * `lead_detail` gets wholesale-replaced by later fetches (e.g. a lead-by-id reload) that do not
   * always populate `project_detail`, even though the value this Input was first set with — the
   * tab/list row that opened this lead — reliably does. Without this cache, anything reading the
   * project name off the live `lead_detail` (the "Send External EOI Form" modal) would go blank
   * right after such a reload even though the name was known a moment earlier.
   */
  private cachedLeadProjectName = '';

  @Input('leaddetail')
  set lead_detail(value: any) {
    this._lead_detail = value;
    const name = EditLeadDetailComponent.extractProjectName(value);
    if (name) this.cachedLeadProjectName = name;
  }

  get lead_detail(): any {
    return this._lead_detail;
  }

  private static extractProjectName(value: any): string {
    const p = value?.project;
    const direct = Array.isArray(p) ? p[0] : p;
    return (typeof direct === 'object' && direct?.Project_Name) || value?.project_detail?.Project_Name || '';
  }

  @Input() tabIndex: any;
  @Input() leadTabIndex: any;
  @Input() isAllowEditAccess: boolean = false;
  @Input() contactUpdate: Observable<any> = null;
  /** Kill-switch for auto-save; edit mode falls back to Save-button-only when false. */
  @Input() autoSaveEnabled: boolean = true;
  @Output() updatedLead = new EventEmitter<boolean>();
  /** Duplicates were merged/closed — other rows changed too, so hosts must reload the whole list, not patch one row. */
  @Output() duplicatesResolved = new EventEmitter<void>();
  @Output() isLeadClose = new EventEmitter<any>();
  @Output() isSavedClosed = new EventEmitter<any>();

  @ViewChild('scrollable', { static: false, read: ElementRef }) scrollbar: ElementRef;
  @ViewChild('historyScrollableElement') historyScrollableElement!: ElementRef;
  @ViewChild('sideLeadScrollableElement') sideTaskScrollableElement!: ElementRef;
  @ViewChild(LeadReportNotesSummaryComponent) reportNotesSummaryComponent: LeadReportNotesSummaryComponent;
  @ViewChildren(CollapseBoxComponent) collapseBoxes: QueryList<CollapseBoxComponent>;
  @ViewChildren(CollapseBoxComponent, { read: ElementRef }) collapseBoxEls: QueryList<ElementRef>;

  addNotesTrigger: Subject<boolean> = new Subject<boolean>();
  options = { componentRestrictions: { country: 'AU' }, types: ['geocode'] };
  images = leadImages;
  common_images = commonImages;
  editLeadForm: FormGroup = null;
  composeEmail: FormGroup = null;
  Editor = ClassicEditor;
  email_Editor = ClassicEditor;
  user_role = Crypto.decrypt(this.localStorageService.GetItem('role'))?.toLowerCase() || '';
  isCalenderDisabled: boolean = false;
  defaultFormSetting = {
    requirement: false,
    enquiry_overview: false,
    notes: false,
    tasks: false,
    appointments: false,
    email: false,
    enquiry_detail: false,
    tags: false,
  };
  loadCom: number = 6;
  all_listing: any[] = [];
  staffList: any[] = [];
  teamList: any[] = [];
  /** Ids of the teams the current user is a member of (used to grant edit access when a lead is owned by a team). */
  userTeamIds: string[] = [];
  isOwnerTeam: boolean = true;
  isAgentResponsibleTeam: boolean = true;
  tagsList: any = [];
  leadTypes: any[] = [];
  leadSource: any[] = [];
  leadStatus: any[] = [];
  listingTypes: any[] = [];
  taskList: any[] = [];
  samePersonLeadExists: any[] = [];
  loading: boolean = false;
  followUpTask: any;
  googleAuthUrl: any;
  emailConnected: boolean = false;
  isReplying: boolean = false;
  leadCalendarManagePermission: any = {};
  emailKeyword: string = '';
  isFetchedEmails: boolean = false;
  samePersonPrompt: boolean = false;
  isMultipleLead: boolean = false;
  /** Controls the "Duplicate Lead Review" merge modal (app-duplicate-lead-merge). */
  showDuplicateReview: boolean = false;
  isInbox: boolean = true;
  isSent: boolean = false;
  samePersonPromptData = [];
  temperature: any[] = [];

  emailCopy: any = null;
  selectedLeadEmails: { inbox: any[]; sent: any[] } = { inbox: [], sent: [] };
  showMailBar: boolean = false;
  openComposeEmail: boolean = false;
  fromOrTop: string = '';
  isNewMail: boolean = false;
  mail_detail: any = null;
  submitEmail: boolean = false;
  lead_contact: any = null;
  lead_buyer: any = null;
  selected_task: any = null;
  leadCategory: any[] = Leads.leadCategory;
  bedrooms = Leads.beds;
  budgetList = [];
  bathrooms = Leads.bathrooms;
  land_size = Leads.land_size;
  age_group = Leads.ageGroup;
  marketPricing = Leads.marketPricing;
  timer_options = Leads.timer_options_new;
  inspection_timer_options = Leads.timer_options;
  current_purpose: any[] = [];
  tenancy_details: any[] = [];
  reason_for_buying: any[] = [];
  reason_for_selling: any[] = [];
  /**
   * Sections the user has filled in during the current edit session.
   * Cleared whenever edit mode is entered or left (see isSectionIncomplete).
   */
  private sectionsFilledInEdit = new Set<string>();
  private _isLeadEdit: boolean = false;
  get isLeadEdit(): boolean {
    return this._isLeadEdit;
  }
  set isLeadEdit(value: boolean) {
    if (this._isLeadEdit !== value) {
      // Leaving edit mode: persist anything still sitting inside the auto-save debounce window.
      // Runs before the flag flips so canAutoSave() still sees an active edit session.
      if (this._isLeadEdit && !value) {
        this.runAutoSave();
      }
      // Each edit session starts fresh (see isSectionIncomplete).
      this.sectionsFilledInEdit.clear();
    }
    this._isLeadEdit = value;
    if (!value) {
      this.autoSaveState = 'idle';
      this.autoSaveError = null;
      this.notifyListenersAfterAutoSave();
    }
  }
  /** Help drawer (Remmi Help / help guides). */
  helpDrawerOpen = false;
  public dup_lead_type: any;
  public lead_status_type = null;
  public follow_up_date = null;
  public follow_up_time = '08:00am';
  public is_follow: boolean = false;
  public is_pick_date: boolean = false;

  mobToggle = false;
  AuthUser: any = null;
  lead_title: string = '';
  currently_marketed: any = [{ tag_name: 'No' }, { tag_name: 'Yes' }];
  tabIndexValue: number = 0;

  smsConversation: any = null;
  smsMessages: any[] = [];
  smsBody = '';
  smsPanelLoading = false;
  smsSendLoading = false;

  tokenInput: any = null;
  assignedLeadEmail = null;
  allAssignedLead: any[] = [];
  message_id = null;
  typeName: any;
  developmentType;
  projectStatus;
  all_property: any[] = [];
  projectList: any[] = [];
  intended_use: any[] = [];
  time_frame: any[] = [];
  indoor_feature: any[] = [];
  outdoor_feature: any[] = [];
  status_type: any[] = [];
  enquiry_Type: any[] = [];
  Established_property: any[] = [];
  contactConflict: boolean = false;
  contactList: any[] = [];
  duplicateContactDraft: DuplicateContactDraft = {};
  related_lead: any[] = [];
  other_leads: any[] = [];
  visible: boolean = false;
  other_lead_visible: boolean = false;
  load_threads = 2;
  keyword_tags = '';
  todo = [];
  tagsDataCopy = [];
  new_tag = [];
  tag_type = null;
  module_type = null;
  new_tagType_popup = false;
  new_tag_popup = false;
  appointmentRows: number = 10;
  appointmentScrollTop: number = 0;
  public follow_priority = null;
  minSelectableDate: Date;
  minDate: Date;
  emailToSuggestion: any[] = [];
  singleClick = false;
  inspectionTime = null;
  /** False until getLeadById has finished and the form is patched — blocks premature updateLead calls. */
  isLeadDetailLoaded = false;
  private leadDetailFetchInFlight = false;

  // ---- Dynamics CRM sync badge/polling ----
  // Live status fields (dynamic_sync_*) live on lead_detail. Poll only while pending/syncing.
  private dynamicSyncPollTimer: ReturnType<typeof setTimeout> = null;
  private dynamicSyncPollAttempt = 0;
  private isDynamicSyncPolling = false;
  dynamicSyncRetrying = false;
  /** Backs off and stays at the last delay, so a long-running push doesn't re-download the whole lead every 7 s. */
  private readonly DYNAMIC_SYNC_POLL_DELAYS_MS = [7000, 15000, 30000];

  // ---- Remmi AI correction indicator ----
  // lead_detail.ai_corrected_fields holds the raw db keys the one-time AI pass changed.
  // Map them to display labels for the summary tooltip.
  private readonly AI_CORRECTED_FIELD_LABELS: { [key: string]: string } = {
    first_name: 'First Name',
    last_name: 'Last Name',
    full_name: 'Full Name',
    email: 'Email',
    mobile_phone: 'Mobile Phone',
    time_frame: 'Time Frame',
    intended_use: 'Intended Use',
    price_range: 'Price Range',
    beds: 'Bedrooms',
    lead_source: 'Lead Source',
    enquiry_type: 'Enquiry Type',
    lead_type: 'Lead Type',
  };

  constructor(
    private formHelper: FormHelper,
    private fb: FormBuilder,
    public api: ApiService,
    public sharedService: SharedService,
    public taskService: TaskService,
    public load: LoadingService,
    public tagMangerService: TagMangerService,
    public listingService: ListingPropertiesService,
    public userService: UserService,
    public lmService: LeadManagementService,
    private _workflowService: WorkflowService,
    private toaster: ToasterService,
    public followUpService: FollowUpService,
    private _toasterService: ToasterService,
    private localStorageService: LocalStorageService,
    private leads: LeadsService,
    private router: Router,
    private dash: DashService,
    private redux: ReduxService,
    private message: MessageService,
    private _projectService: ProjectService,
    private _CalendarAPIService: CalendarAPIService,
    private leadSmsService: LeadSmsService,
    private domSanitizer: DomSanitizer,
    private hostEl: ElementRef,
    private alertService: AlertService,
    private _settingService: SettingService,
    private activityTracker: ActivityTrackerService,
    private followUpOptionsService: FollowUpOptionsService,
    private remmiConfirm: RemmiConfirmService
  ) {
    this.minSelectableDate = new Date();
    this.minSelectableDate.setHours(0, 0, 0, 0);
    this.minDate = new Date();
    this.composeEmail = this.fb.group({
      to: [[], Validators.required],
      subject: ['', Validators.required],
      message: ['', Validators.required],
    });
    this.editLeadForm = this.fb.group({
      first_name: null,
      last_name: null,
      sub_office_id: null,
      mobile_phone: null,
      telephone: null,
      temperature: null,
      email: null,
      suburb: null,
      postcode: null,
      countryregion: null,
      lead_status_log: null,
      project: null,
      listing_project: null,
      apartment: null,
      // Requirement
      listing_type: null,
      value: null,
      beds: null,
      baths: null,
      car: null,
      pools: null,
      spa: null,
      land_area: null,
      internal_size: null,
      external_area: null,
      address: null,
      tenancy_details: null,
      current_purpose: null,
      tenancy_end_date: null,
      reason_for_buying: null,
      reason_for_selling: null,
      intended_use: null,
      time_frame: null,
      price_range: null,
      age_group: null,
      agent_responsible: null,
      Owner: null,
      application_submitted: false,
      application_submitted_date: null,
      time: null,
      lead_source: null,
      enquiry_type: null,
      lead_status: null,
      not_interested: null,
      lead_eoi: null,
      lead_type: null,
      listing: null,
      property: null,
      lead_category: null,
      established_property: null,
      features: null,
      indoor_feature: null,
      outdoor_feature: null,
      min_land_area: null,
      max_land_area: null,
      created_at: null,
      development_type: null,
      project_address: [''],
      project_suburb: [''],
      project_status: null,
      lot_quantity: [''],
      current_market: [''],
      product_mix: [''],
      levels: [''],
      amenities: [''],
      report_notes: [''],
      report_notes_summary: [''],
      inspection_dateTime: [''],
      inspection_booked: [''],
      inspection_complete: [''],
      inspection_cancelled: [''],
      latest_inspection_status: [''],

      // not use
      lead_notes: null,
      full_name: null,
      contactid: null,
      agencyid: null,
      // Agent leads only — the buyer is its own contact, separate from the agent in contactid
      buyer_detail_exist: false,
      buyer_contactid: null,
      study: null,
      contact_method: null,
      nobedroom: null,

      // lead Information
      project_information_sent: null,
      project_information_sent_date: null,
      agreement_sent_date: null,
      agreement: null,
      personal_briefing_date: null,
      personal_briefing_time: null,
      personal_briefing_complete: null,
      onsite_training_date: null,
      onsite_training_time: null,
      onsite_training_complete: null,
      comments: null,
    });
  }

  getUser() {
    this.redux.getUser().subscribe(response => {
      this.AuthUser = JSON.parse(JSON.stringify(response?.data ?? response));
    });
  }

  ngOnInit(): void {
    this.getUser();
    this.isCalenderDisabled = this.user_role != 'master admin' && this.user_role != 'operations manager';
    if (this.isCalenderDisabled) {
      this.editLeadForm.get(`created_at`)?.disable(); // Disable 'created_at' field in the form
    }
    this.getSubOffices();

    // Subscribe to task updates and get tasks when updated
    this.taskUpdate?.subscribe(rex => {
      if (rex === true) {
        this.getTasks();
      }
    });
    this.eoiUpdate?.subscribe(rex => {
      if (rex === true) {
        this.getEOIList();
      }
    });
    // Set the lead title based on available lead details (property, listing, project, etc.)

    // Subscribe to contact updates and refresh the lead details when updated
    this.contactUpdate?.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        let findIndex = this.lmService.tab_levels?.findIndex(ele => ele?._id == this.lead_detail?._id);
        this.leads.clearLeadByIdCache(this.lead_detail?._id);
        this.getLeadDetail();
        setTimeout(() => {
          this.lmService.tab_levels[findIndex].name =
            `Lead - ${this.lead_detail?.listing_project?.project || this.lead_detail?.listing_project?.listing}: ${this.lead_detail?.full_name}`;
        }, 3000);
      },
    });

    // Call various functions to initialize lead and calendar data
    this.getLeadTags();
    this.getAllLead();
    this.getStaffUsers();
    this.getTeamList();
    this.getUserTeamIds();
    this.lmService.getPropertyFeatures();

    // Subscribe to screen size updates and toggle mobile view when the width is below 567px
    this.dash?.screenSize$?.subscribe({
      next: size => {
        if (size.width < 567) {
          this.mobToggle = true;
        } else {
          this.mobToggle = false;
        }
      },
    });

    // AI summary refresh on report_notes / inspection changes is owned by LeadReportNotesSummaryComponent,
    // which watches the same form controls; calling it here too sent duplicate paid ai-lead-summary requests.
    ['inspection_booked', 'inspection_complete', 'inspection_cancelled'].forEach(controlName => {
      this.editLeadForm
        .get(controlName)
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe(() => {
          this.syncInspectionStatus();
        });
    });

    this.setupAutoSave();
  }

  /** Set as soon as the tab is torn down; see getLeadDetail. */
  private isDestroyed = false;

  ngOnDestroy(): void {
    this.isDestroyed = true;
    // Persist anything still inside the auto-save debounce window before the tab goes away.
    this.runAutoSave();
    this.destroy$.next();
    this.destroy$.complete();

    // Clear related lead data and reset the refresh lead model flag
    this.related_lead = [];
    this.other_leads = [];
    this.lmService.refresh_lead_model = true;
    this.stopDynamicSyncPolling();
  }

  ngAfterViewInit(): void {
    this.getLeadDetail();
    // Fetch the lead contact details and prompt if the lead is the same person
    this.getLeadContact(this.lead_detail?.contactid);
    this.leadSamePersonPrompt();
  }

  removeAssignment() {
    this.remmiConfirm
      .ask({
        title: 'Remove Assigned Contact?',
        message: 'The contact will be unlinked from this lead. The contact record itself is kept.',
        subject: this.lead_detail?.first_name,
        confirmLabel: 'Remove',
        tone: 'warning',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          // Get the assignment details of the current lead and remove the contact assignment
          this.leads.getAssignment(this.lead_detail?._id).subscribe({
            next: response => {
              // Remove the contact ID from the lead details
              this.lead_detail.contactid = null;
              delete this.lead_detail.contactid;

              // Show a success toaster message indicating the assignment removal
              this._toasterService.Success(response.message);
            },
            error: () => {
              // Show an error message if something goes wrong
              this._toasterService.Error('Something went wrong');
            },
          });
        }
      });
  }

  close() {
    // Set 'visible' to false, which likely hides a UI element such as a modal or a dialog
    this.visible = false;
  }

  show() {
    this.visible = true;
  }

  getLeadDetail() {
    // Nothing reloads a lead whose tab has gone. `destroy$` cannot enforce that by itself: it has
    // already completed, so a caller firing afterwards (a late save, tag or contact response) would
    // start a fresh request that nothing tears down, and its response would auto-open the closed
    // lead's related tabs beside whichever lead was opened next.
    const leadId = this.lead_detail?._id;
    if (this.isDestroyed || !leadId || this.leadDetailFetchInFlight) {
      return;
    }
    this.leadDetailFetchInFlight = true;
    this.isLeadDetailLoaded = false;

    this.leads
      .getLeadById(leadId)
      .pipe(
        finalize(() => {
          this.leadDetailFetchInFlight = false;
        }),
        // Tear the request down with the tab: closing a lead before this answers used to let the response
        // run anyway and auto-open that lead's related tabs next to whichever lead was opened after it.
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          const { status, data } = response;
          if (status) {
            // If the response is successful, update lead details with received data

            let officeName = Crypto.decrypt(this.localStorageService.GetItem('office_name'));

            this.lead_detail = {
              ...data,
              // alternate_fullname: data?.alternate_first_name && data?.alternat_last_name ? `${data?.alternate_first_name} ${data?.alternat_last_name}` : null,
              established_property: data?.established_property ? data?.established_property : officeName == 'YPM Avanti' ? 'New' : null,
              listing_address: this.lead_detail?.listing ? this.lead_detail?.listing : this.lead_detail?.listing_detail?.property_address,
              project_address: this.lead_detail?.listing_project?.project
                ? this.lead_detail?.listing_project?.project
                : this.lead_detail?.listing_project?.listing
                  ? this.lead_detail?.listing_project?.listing
                  : data?.project_address,
            };

            this.getProjectAppointments(this.lead_detail);
            this.refreshLeadContact();

            // The AI assistant can deep-link straight to a lead's Appointment tab (index 1).
            // Consume the one-shot hint so a later refresh doesn't re-switch the tab.
            if (this.lmService.aiLeadTab === 'appointment') {
              this.tabIndexValue = 1;
              this.lmService.aiLeadTab = null;
            }
            // Action Today's "Open Inspection" (REM-3537_2026-10-14 §8): the inspection lives on the lead
            // itself, not as an appointment row, so land on Lead Overview with its INSPECTION box open.
            if (this.lmService.aiLeadTab === 'inspection') {
              this.tabIndexValue = 0;
              this.lmService.aiLeadTab = null;
              this.focusInspectionBox();
            }
            if (this.lmService.aiLeadTab === 'horizon') {
              const horizonIndex = this.tabs.findIndex(tab => tab.id === 'horizon');
              if (horizonIndex >= 0) {
                this.tabIndexValue = horizonIndex;
              }
              this.lmService.aiLeadTab = null;
            }

            if (this.lead_detail?.lead_eoi) {
              if (!this.tabs?.find(tab => tab.id == 'eoi')) {
                this.tabs.push({
                  id: 'eoi',
                  flag: 'eoi',
                  label: 'EOI',
                  icon: 'assets/img/menuIcon/project1.svg',
                  action: () => {
                    // this.lmService.backToEOIList = false;
                    this.router.navigateByUrl(`/projects/edit_/${this.lead_detail?.project}/eoi?id=${this.lead_detail?.lead_eoi}`);
                  },
                });
              }
            }

            // Sort related leads by creation date
            this.related_lead =
              data?.related_leads?.length > 0
                ? data?.related_leads.sort((a, b) => {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                  })
                : [];

            this.other_leads =
              data?.other_lead_inquiries?.length > 0
                ? data?.other_lead_inquiries.sort((a, b) => {
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                  })
                : [];

            // Set tags for the lead if available
            this.todo = data?.tag?.length > 0 ? data?.tag : [];

            // Check if lead has required fields and show error if any are missing
            if (!this.lead_detail?.first_name && !this.lead_detail?.last_name && !this.lead_detail?.full_name) {
              this.toaster.Error('We have detected an issue with the lead parser. Please inform your Operations Manager');
            } else if (!this.lead_detail?.contactid || this.lead_detail?.hasOwnProperty('contactid') == false) {
              this.convertToContact(this.lead_detail);
            }
          } else {
            this._toasterService.Error('Something went wrong');
          }

          // Patch form first, then load dependent data so auto-save never runs on an empty form
          this.patchFormValuesOnLoad();
          this.getTasks();
          this.loadCommission();
          this.startDynamicSyncPolling();

          if (this.related_lead?.length > 0) {
            // Torn down with the component so a lead closed or switched away from cannot push its related tabs into the next lead.
            timer(1500)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                this.lmService.leadLoader = true;

                this.related_lead.forEach(el => {
                  const foundedValue = this.lmService.tab_levels?.find(value => value?._id === el?._id);
                  if (!foundedValue) {
                    this.editRecord(el);
                  }
                });

                this.lmService.leadLoader = false;
              });
          } else {
            this.lmService.leadLoader = false;
          }
        },
        error: () => {
          this.isLeadDetailLoaded = false;
          this._toasterService.Error('Something went wrong');
        },
      });
  }

  project_appointment_list: any = [];
  getProjectAppointments(record) {
    this._CalendarAPIService.getProjectContactEventsById(record?.contactid, record?._id, record?.project).subscribe({
      next: response => {
        this.project_appointment_list = response.data;
      },
    });
  }

  // ---------- Remmi AI correction indicator ----------

  /** True when the one-time Remmi AI pass corrected the given raw lead field on this lead. */
  isAiCorrected(field: string): boolean {
    return Array.isArray(this.lead_detail?.ai_corrected_fields) && this.lead_detail.ai_corrected_fields.includes(field);
  }

  /** Whether Remmi AI corrected any field on this lead (drives the summary marker). */
  get hasAiCorrections(): boolean {
    return Array.isArray(this.lead_detail?.ai_corrected_fields) && this.lead_detail.ai_corrected_fields.length > 0;
  }

  /** Comma-separated display labels of the AI-corrected fields, for the summary tooltip. */
  get aiCorrectedFieldLabels(): string {
    if (!this.hasAiCorrections) {
      return '';
    }
    return this.lead_detail.ai_corrected_fields.map((f: string) => this.AI_CORRECTED_FIELD_LABELS[f] || f).join(', ');
  }

  // ---------- Dynamics CRM sync badge / polling / retry ----------

  /** Dynamics badge only shows for project leads on a Dynamics-enabled project (fields present). */
  get isDynamicSyncVisible(): boolean {
    return this.lead_detail?.dynamic_sync_status !== undefined && this.lead_detail?.dynamic_sync_status !== null;
  }

  /** True while a sync is in progress — show spinner, hide retry. */
  get isDynamicSyncInProgress(): boolean {
    const s = this.lead_detail?.dynamic_sync_status;
    return s === 'pending' || s === 'syncing' || this.dynamicSyncRetrying;
  }

  /** Begin polling if the current status is still in-flight. No-op otherwise. */
  startDynamicSyncPolling(): void {
    const status = this.lead_detail?.dynamic_sync_status;
    if (status !== 'pending' && status !== 'syncing') {
      return;
    }
    this.stopDynamicSyncPolling();
    this.isDynamicSyncPolling = true;
    document.addEventListener('visibilitychange', this.onDynamicSyncVisibilityChange);
    this.scheduleDynamicSyncPoll();
  }

  /** Arms the next poll unless polling stopped or the browser tab is hidden (the visibility handler resumes it). */
  private scheduleDynamicSyncPoll(): void {
    clearTimeout(this.dynamicSyncPollTimer);
    this.dynamicSyncPollTimer = null;
    if (!this.isDynamicSyncPolling || document.hidden) {
      return;
    }
    const delays = this.DYNAMIC_SYNC_POLL_DELAYS_MS;
    const delay = delays[Math.min(this.dynamicSyncPollAttempt, delays.length - 1)];
    this.dynamicSyncPollTimer = setTimeout(() => {
      this.dynamicSyncPollTimer = null;
      if (document.hidden) {
        return;
      }
      this.dynamicSyncPollAttempt++;
      this.pollDynamicSyncStatus();
    }, delay);
  }

  private readonly onDynamicSyncVisibilityChange = (): void => {
    if (document.hidden) {
      clearTimeout(this.dynamicSyncPollTimer);
      this.dynamicSyncPollTimer = null;
    } else if (!this.dynamicSyncPollTimer) {
      this.scheduleDynamicSyncPoll();
    }
  };

  /** Fetch latest lead and patch only the dynamic_sync_* fields so the open form is never disturbed. */
  private pollDynamicSyncStatus(): void {
    const leadId = this.lead_detail?._id;
    if (!leadId) {
      this.stopDynamicSyncPolling();
      return;
    }
    this.leads.getLeadById(leadId).subscribe({
      next: response => {
        const data = response?.data;
        if (response?.status && data) {
          this.lead_detail = {
            ...this.lead_detail,
            dynamic_sync_status: data?.dynamic_sync_status,
            dynamic_sync_error: data?.dynamic_sync_error,
            dynamic_sync_at: data?.dynamic_sync_at,
            dynamic_sync_attempts: data?.dynamic_sync_attempts,
            dynamic_sync_exhausted: data?.dynamic_sync_exhausted,
            dynamic_sync_queued_at: data?.dynamic_sync_queued_at,
            dynamic_sync_updated_at: data?.dynamic_sync_updated_at,
          };
          const s = data?.dynamic_sync_status;
          if (s === 'synced' || s === 'failed') {
            this.dynamicSyncRetrying = false;
            this.stopDynamicSyncPolling();
          }
        }
        this.scheduleDynamicSyncPoll();
      },
      error: () => {
        // Keep polling on a transient error; status will resolve on a later tick.
        this.scheduleDynamicSyncPoll();
      },
    });
  }

  stopDynamicSyncPolling(): void {
    this.isDynamicSyncPolling = false;
    this.dynamicSyncPollAttempt = 0;
    clearTimeout(this.dynamicSyncPollTimer);
    this.dynamicSyncPollTimer = null;
    document.removeEventListener('visibilitychange', this.onDynamicSyncVisibilityChange);
  }

  /** Retry button → re-queue the failed push. Optimistically flips badge to pending then polls. */
  retryDynamicSync(): void {
    const leadId = this.lead_detail?._id;
    if (!leadId || this.dynamicSyncRetrying) {
      return;
    }
    this.dynamicSyncRetrying = true;
    this.lead_detail = { ...this.lead_detail, dynamic_sync_status: 'pending', dynamic_sync_error: null };
    this.leads.resyncLeadToDynamic(leadId).subscribe({
      next: response => {
        if (response?.status) {
          this.lead_detail = { ...this.lead_detail, dynamic_sync_status: response?.data?.dynamic_sync_status || 'pending' };
          this.startDynamicSyncPolling();
        } else {
          // 422 etc: Dynamics not enabled / invalid lead — retry won't help.
          this.dynamicSyncRetrying = false;
          this.lead_detail = { ...this.lead_detail, dynamic_sync_status: 'failed' };
          this._toasterService.Error(response?.message || 'Unable to re-sync lead to Dynamics');
        }
      },
      error: error => {
        this.dynamicSyncRetrying = false;
        this.lead_detail = { ...this.lead_detail, dynamic_sync_status: 'failed' };
        this._toasterService.Error(error?.error?.message || error?.message || 'Unable to re-sync lead to Dynamics');
      },
    });
  }

  /**
   * Opens the header notes panel. If this lead is linked to a project, the notes
   * are scoped to that project id (same behaviour as the /projects/edit_/<id> URL).
   */
  openLeadNotes() {
    const projectId = this.lead_detail?.project || this.editLeadForm?.controls?.['project']?.value || null;
    const leadName = this.lead_detail?.first_name || null;
    const leadEmail = this.lead_detail?.email || null;
    this.alertService.openNotesPanel(projectId, leadName, leadEmail);
  }

  isAllowEdit() {
    let userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
    let isAdminRole = userRole === 'master admin' || userRole === 'operations manager';
    const currentUserId = this.api.getUserId();
    let leadAgentId = this.lead_detail?.Owner === currentUserId || this.lead_detail?.agent_responsible === currentUserId;

    // When the lead is owned by (or its agent responsible is) a team, the Owner /
    // agent_responsible field holds a team id. Grant edit access if the current
    // user is a member of that team.
    const ownedThroughTeam =
      (this.lead_detail?.isOwnerTeam && this.userTeamIds.includes(this.lead_detail?.Owner)) ||
      (this.lead_detail?.isAgentResponsibleTeam && this.userTeamIds.includes(this.lead_detail?.agent_responsible));

    return this.isAllowEditAccess || isAdminRole || leadAgentId || ownedThroughTeam ? true : false;
  }

  /**
   * Switch a read-only section into edit mode by clicking it directly,
   * so the user doesn't have to press the Edit (pencil) button first.
   * No-ops if already editing or the user lacks edit permission.
   */
  enableEditMode() {
    if (this.isLeadEdit) return;
    if (this.isAllowEdit()) {
      this.isLeadEdit = true;
    } else {
      this.toaster.Error('Do not have access to edit this lead');
    }
  }

  /**
   * Lead Overview section headers (view mode only): the chevron icon toggles
   * show/hide, clicking the header body enters edit mode. Only the body click
   * emits an 'edit-' action; guard on !isLeadEdit avoids re-firing the
   * permission toaster while already editing.
   */
  onSectionHeaderClick(action: string): void {
    if (!this.isLeadEdit && action?.startsWith('edit-')) {
      this.enableEditMode();
    }
  }

  editRecord(record, isManualEdit = false) {
    if (isManualEdit) {
      this.visible = false;
      this.other_lead_visible = false;
      let lead_title: string | null = null;
      if (record?.listing_project) {
        lead_title = `Lead - ${record.listing_project.Project_Name ?? record.listing_project.project ?? record.listing_project.listing ?? ''}: ${record?.full_name ?? ''}`;
      } else {
        const fallbackTitle = record?.property || record?.listing_detail?.property_address || 'N/A';
        lead_title = `Lead - ${fallbackTitle}: ${record?.full_name ?? ''}`;
      }
      this.lmService.addTabInLevels(lead_title, record?._id, record, null, 'lead', true);
    } else {
      // Refresh lead model and build lead name for the tab title
      this.lmService.refresh_lead_model = true;

      let lead_title: string | null = null;

      if (record?.listing_project) {
        lead_title = `Lead - ${record.listing_project.Project_Name ?? record.listing_project.project ?? record.listing_project.listing ?? ''}: ${record?.full_name ?? ''}`;
      } else {
        const fallbackTitle = record?.property || record?.listing_detail?.property_address || 'N/A';
        lead_title = `Lead - ${fallbackTitle}: ${record?.full_name ?? ''}`;
      }

      let inActive_leadStatus = [...(this.leadStatus?.filter(x => x?.is_active === false)?.map(x => x?.tag_name) || []), 'Duplicate'];

      // let userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));
      // let isAdminRole = userRole === 'master admin' || userRole === 'operations manager';
      // if (isAdminRole) {
      //   this.lmService.addTabInLevels(lead_title, record?._id, record, null, 'lead', true);
      // } else {
      if (record?.status_type === 'Active' && !inActive_leadStatus.includes(record?.lead_status)) {
        this.lmService.addTabInLevels(lead_title, record?._id, record, null, 'lead', true);
      }
      // }
    }
  }

  checkConditionValue(value) {
    return value === 'Seller' || value === 'Prospective Seller' ? true : false;
  }

  onUpdateMSelect(param, event: any) {
    // If the event is null, reset the fields and enable the form fields
    console.log('event', event);

    if (param === 'contact') {
      // If the event is null, reset the fields and enable the form fields
      if (event === null) {
        this.editLeadForm.patchValue({
          first_name: null,
          last_name: null,
          mobile_phone: null,
          telephone: null,
          email: null,
          suburb: null,
          postcode: null,
        });
      } else {
        this.patchEditForm(event._id);
      }
    } else {
      console.log('company', event);
      if (event) {
        this.editLeadForm.patchValue({
          agencyid: event?._id,
        });
      } else {
        this.editLeadForm.patchValue({
          agencyid: null,
        });
      }
    }
  }

  patchEditForm(id) {
    this.userService.getContactUser(id).subscribe({
      next: response => {
        this.lead_detail = {
          ...this.lead_detail,
          full_name: response?.data?.full_name,
          email: response?.data?.email,
          mobile_phone: response?.data?.mobile_no,
          contact_address: response?.data?.contact_address,
          contactid: response?.data?._id,
        };

        this.editLeadForm.patchValue({
          contactid: response?.data?._id,
          first_name: response?.data?.first_name,
          last_name: response?.data?.last_name,
          full_name: response?.data?.full_name,
          mobile_phone: response?.data?.mobile_no,
          telephone: response?.data?.telephone || null,
          email: response?.data?.email,
          suburb: typeof response?.data?.suburb == 'object' ? response?.data?.suburb?.suburb : response?.data?.suburb,
          postcode: response?.data?.post_code,
        });
      },
    });
  }

  subOfficeList: any[] = [];
  private subOfficeDefaulted = false;
  private leadPatched = false;
  getSubOffices() {
    this._settingService.getSubOfficeList().subscribe({
      next: (response: any) => {
        this.subOfficeList = response?.data || [];
        this.applyDefaultSubOffice();
      },
    });
  }

  /**
   * Defaults the Sub Office field to the first option, same as the other office pickers.
   * Waits for `patchFormValuesOnLoad`'s patch to land first — it can arrive after the sub office
   * list and would otherwise overwrite the default with the lead's own (empty) value.
   */
  applyDefaultSubOffice() {
    if (!this._settingService.isSubOfficeAutoDefaultEnabled()) {
      return;
    }
    if (this.subOfficeDefaulted || !this.subOfficeList.length || !this.leadPatched) return;
    this.subOfficeDefaulted = true;
    if (!this.editLeadForm.get('sub_office_id')?.value) {
      this.editLeadForm.get('sub_office_id')?.setValue(this.subOfficeList[0]._id);
    }
  }

  patchFormValuesOnLoad() {
    // Set lead time to the nearest time if not already set
    // if (!this.lead_detail?.time) {
    //   this.lead_detail.time = this.followUpService.getNearestTime(this.lead_detail.created_at, 0);
    // }

    // Extract lead category and created_at for patching form values
    const { lead_category, created_at } = this.lead_detail;

    this.dup_lead_type = this.lead_detail?.lead_type;
    this.lead_status_type = this.lead_detail?.status_type ? this.lead_detail?.status_type : 'Active';
    this.isOwnerTeam = this.lead_detail?.isOwnerTeam;
    this.isAgentResponsibleTeam = this.lead_detail?.isAgentResponsibleTeam;
    let inspection_dateTime = this.lead_detail?.inspection_dateTime?.split('T');
    let personal_briefing_date = this.lead_detail?.personal_briefing_date ? this.lead_detail?.personal_briefing_date?.split('T') : null;
    let onsite_training_date = this.lead_detail?.onsite_training_date ? this.lead_detail?.onsite_training_date?.split('T') : null;

    this.inspectionTime = inspection_dateTime
      ? inspection_dateTime[1] && inspection_dateTime[1] !== '00:00:00.000Z'
        ? inspection_dateTime[1]
        : null
      : null;

    // Patch lead details and tenancy end date to the form
    this.editLeadForm.patchValue({ ...this.lead_detail, created_at: new Date(created_at) });
    // Leads saved before buyer details existed carry neither key, and patchValue skips missing
    // keys — set both so a previous lead's buyer can't linger on this one.
    this.editLeadForm.patchValue({
      buyer_detail_exist: !!this.lead_detail?.buyer_detail_exist,
      buyer_contactid: this.lead_detail?.buyer_contactid ?? null,
    });
    this.getLeadBuyer(this.lead_detail?.buyer_contactid);
    this.editLeadForm.patchValue({
      tenancy_end_date: this.lead_detail?.tenancy_end_date ? new Date(this.lead_detail?.tenancy_end_date) : null,
      inspection_dateTime: inspection_dateTime ? (inspection_dateTime[0] ? new Date(inspection_dateTime[0]) : null) : null,
      project_information_sent_date: this.lead_detail?.project_information_sent_date
        ? new Date(this.lead_detail?.project_information_sent_date)
        : null,
      agreement_sent_date: this.lead_detail?.agreement_sent_date ? new Date(this.lead_detail?.agreement_sent_date) : null,
      personal_briefing_date: personal_briefing_date ? new Date(personal_briefing_date[0]) : null,
      personal_briefing_time: personal_briefing_date ? personal_briefing_date[1] : null,
      onsite_training_date: onsite_training_date ? new Date(onsite_training_date[0]) : null,
      onsite_training_time: onsite_training_date ? onsite_training_date[1] : null,

      project_address: this.lead_detail?.project_address,
      address: this.lead_detail?.address,
      temperature: this.lead_detail?.temperature
        ? this.lead_detail?.temperature
        : this.lead_detail?.lead_status === 'No Further Interest'
          ? 'Cold'
          : 'Warm',
    });
    this.leadPatched = true;
    this.applyDefaultSubOffice();

    // Based on lead category, call corresponding function to fetch data
    switch (lead_category) {
      case 'project':
        this.getProjects();
        this.getEOIList();
        break;
      case 'listing':
        this.getAllListings();
        break;
      case 'property':
      default:
        this.getProperty();
        break;
    }

    // Allocate tags and manage lead status based on the current lead details
    this.allocateTagsToFields('lead-status');
    this.manageLeadStatus(true);
    this.isLeadDetailLoaded = true;
    // A freshly loaded form is by definition already saved — baseline it so the patches
    // above (and any later reload) never look like a user edit to auto-save.
    this.lastSavedSnapshot = this.snapshotForm();
    this.confirmedReportNotes = this.editLeadForm.controls['report_notes']?.value || '';
  }

  GetListingWorkflowProcesses() {
    // Mark the workflow process tab as opened and initialize the process list
    this._workflowService.isWorkflowProcessTabOpened = true;
    this._workflowService.ActiveProcessList = [];
    this._workflowService.processRecordId = this.lead_detail?._id;

    // Call service to fetch all workflow processes for the current lead
    this._workflowService
      .GetAllProcessesByModule(
        this.lead_detail?._id,
        this._workflowService.module_array.filter(res => res.name === 'Lead Management')?.[0]?.module_value
      )
      .subscribe({
        next: response => {
          try {
            const { status, data, meta } = response;
            if (status) {
              // If the response is successful, process the workflow data
              this._workflowService.ActiveProcessList = data?.map(
                result =>
                  (result = {
                    ...result,
                    module: Utils.capitalizePhrase(result?.module?.split('_')?.join(' ')?.trim() || ''),
                  })
              );

              // Update the pagination details for centralized processes
              this._workflowService.centralizedProcessPagination = {
                ...this._workflowService.centralizedProcessPagination,
                per_page: 10,
                total_pages: Math.ceil(meta?.pagination?.total / 10),
                total: meta?.pagination?.total,
                numberOfRecords: this._workflowService.ActiveProcessList?.length > 10 ? 10 : this._workflowService.ActiveProcessList?.length,
              };
            } else {
              // If no data, reset the process list
              this._workflowService.ActiveProcessList = [];
            }
          } catch (error) {
            // Reset process list in case of any error during processing
            this._workflowService.ActiveProcessList = [];
          }
        },
      });
  }

  /**
   * Handles the change in address based on the type ('project_address' or 'location').
   * It updates the form or location property based on the type of address received.
   *
   * @param address The address object containing address components.
   * @param type The type of address change ('project_address' or 'location').
   */
  handleAddressChange(address: any, type) {
    if (type === 'project_address') {
      address?.address_components.forEach(el => {
        el.types.forEach(type => {
          if (type === 'locality') {
            this.editLeadForm.patchValue({
              project_suburb: el?.long_name,
            });
          }
        });
      });
    }
  }

  /**
   * Fetches and checks if there are any leads with the same person.
   * It updates the state of multiple lead flag and stores the same person prompt data.
   */
  leadSamePersonPrompt() {
    this.leads.getLeadSamePersonPrompt(this.lead_detail?._id).subscribe({
      next: response => {
        if (response.status == true) {
          this.isMultipleLead = true;
          this.samePersonPromptData = response.data;
        } else {
          this.isMultipleLead = false;
        }
      },
    });
  }

  /**
   * Matches the duplicate modal can actually close: same project only (client, 2026-10-14). Until
   * same_person_prompt returns `project_id` (docs/backend/duplicate-leads-remaining.md §4) every
   * match is counted, as before.
   */
  get sameProjectDuplicateCount(): number {
    const matches: any[] = this.samePersonPromptData ?? [];
    const projectId = this.lead_detail?.project?._id ?? this.lead_detail?.project;
    if (!projectId || !matches.some(item => item?.project_id !== undefined)) {
      return matches.length;
    }
    return matches.filter(item => String(item?.project_id ?? '') === String(projectId)).length;
  }

  /**
   * Opens the "Duplicate Lead Review" modal for the current lead. Wire this to a
   * "Mark Duplicate" button or to the flow that closes a duplicate lead. The
   * modal self-fetches the same-person peers and full detail for each.
   */
  openDuplicateReview() {
    if (!this.lead_detail?._id) return;
    this.showDuplicateReview = true;
  }

  /** After a merge: refresh the retained lead so the copied/merged data shows. */
  onDuplicateMerged(result?: MergeResult) {
    this.showDuplicateReview = false;
    this.markOpenLeadClosed(result?.duplicateLeadIds);
    this.leads.clearLeadByIdCache(this.lead_detail?._id);
    this.getLeadDetail();
    this.leadSamePersonPrompt();
    this.duplicatesResolved.emit();
    // NOTE: closing the other duplicate tabs is a host/navigation concern — the
    // duplicates are now flagged 'Duplicate' + duplicate_of, so re-opening the
    // leads list will drop them from the active pipeline.
  }

  /** After Skip & Close: duplicates were flagged but nothing was copied. */
  onDuplicatesSkipped(result?: SkipResult) {
    this.showDuplicateReview = false;
    if (this.markOpenLeadClosed(result?.duplicateLeadIds)) {
      this.leads.clearLeadByIdCache(this.lead_detail?._id);
      this.getLeadDetail();
    }
    this.leadSamePersonPrompt();
    this.duplicatesResolved.emit();
  }

  /**
   * When the lead open in this tab is one of the duplicates just closed, its save payload still
   * carries the `status_type` it loaded with (see onSaveLeadDetail). Any later background save
   * (report-notes summary, auto-save, contact note) would then reopen it as Active, so the agent
   * had to close it twice. Switch the local status first so those saves keep it closed.
   */
  private markOpenLeadClosed(duplicateLeadIds: string[] = []): boolean {
    if (!this.lead_detail?._id || !duplicateLeadIds.includes(this.lead_detail._id)) {
      return false;
    }
    this.lead_status_type = DUPLICATE_LEAD_STATUS;
    this.lead_detail = { ...this.lead_detail, status_type: DUPLICATE_LEAD_STATUS };
    return true;
  }

  /**
   * Retrieves contact information based on the provided contact ID.
   * If a valid contact ID is provided, it fetches the corresponding contact details.
   *
   * @param contact_id The ID of the contact to fetch (defaults to null).
   */
  /**
   * The lead opens with the grid row, which may not carry `contactid`, so the ngAfterViewInit fetch
   * can miss the linked contact. Re-read it once the full lead is in, unless it's already loaded,
   * so the Linked Contact card (with its alternate contacts, REM-3497) always shows.
   */
  private refreshLeadContact() {
    const contactId = this.lead_detail?.contactid;
    if (!contactId) {
      return;
    }
    if (String(this.lead_contact?._id ?? '') !== String(contactId)) {
      this.getLeadContact(contactId);
    }
  }

  isSavingAlternate = false;

  /**
   * REM-3497: saves a lead that was linked before alternate contacts existed onto its contact, so
   * both names stay and the next contact save no longer overwrites this lead. Reuses
   * convert-to-contact with the lead's own contactid (docs/backend/REM-3497-alternate-existing-leads.md).
   * Success is confirmed by re-reading the contact, since an older backend replies "already
   * assigned" without saving anything.
   */
  addLeadAsAlternate(label: string) {
    const leadId = this.lead_detail?._id;
    const contactId = this.lead_detail?.contactid;
    if (!leadId || !contactId || this.isSavingAlternate) {
      return;
    }
    this.isSavingAlternate = true;
    this.leads
      .convertLeadToContact(leadId, { contactid: contactId, createnew: false, add_as_alternate: true, alternate_label: label ?? null })
      .pipe(switchMap(() => this.userService.getUserById(contactId)))
      .subscribe({
        next: response => {
          this.isSavingAlternate = false;
          this.lead_contact = response?.data;
          const isSaved = (this.lead_contact?.alternate_contacts ?? []).some(item => String(item?.lead_id ?? '') === String(leadId));
          if (isSaved) {
            this._toasterService.Success('Saved As Alternate Contact');
          } else {
            this._toasterService.Error('Could Not Save As Alternate Contact. Please Try Again Later.');
          }
        },
        error: () => {
          this.isSavingAlternate = false;
          this._toasterService.Error('Could Not Save As Alternate Contact. Please Try Again Later.');
        },
      });
  }

  getLeadContact(contact_id = null) {
    if (contact_id != '' && contact_id != null) {
      this.userService.getUserById(contact_id).subscribe({
        next: response => {
          this.lead_contact = response.data;
        },
      });
    }
  }

  get showLeadBuyer(): boolean {
    return (
      this.editLeadForm.controls['lead_type'].value == 'Agent' &&
      !!this.editLeadForm.controls['buyer_detail_exist'].value &&
      !!this.editLeadForm.controls['buyer_contactid'].value &&
      !!this.lead_buyer
    );
  }

  getLeadBuyer(buyerContactId: string = null): void {
    if (!buyerContactId) {
      this.lead_buyer = null;
      return;
    }
    if (String(this.lead_buyer?._id ?? '') === String(buyerContactId)) {
      return;
    }
    this.userService.getUserById(buyerContactId).subscribe({
      next: response => {
        this.lead_buyer = response?.data ?? null;
      },
    });
  }

  onBuyerSelect(event: any): void {
    if (!event?._id) {
      this.lead_buyer = null;
      this.editLeadForm.patchValue({ buyer_contactid: null });
      return;
    }
    if (event._id === this.editLeadForm.controls['contactid'].value) {
      this._toasterService.Error('The buyer must be a different contact from the agent');
      return;
    }
    this.editLeadForm.patchValue({ buyer_contactid: event._id });
    this.getLeadBuyer(event._id);
  }

  onBuyerToggle(event: any): void {
    if (event?.checked || !this.editLeadForm.controls['buyer_contactid'].value) {
      return;
    }
    // Keep the section open until the user confirms the buyer should come off the lead.
    this.editLeadForm.patchValue({ buyer_detail_exist: true });
    this.remmiConfirm
      .ask({
        title: 'Remove Buyer?',
        message: 'The buyer will be removed from this lead. The agent stays linked to the lead.',
        subject: this.lead_buyer?.full_name,
        confirmLabel: 'Remove Buyer',
        tone: 'danger',
      })
      .subscribe(isConfirmed => isConfirmed && this.confirmRemoveBuyer());
  }

  confirmRemoveBuyer(): void {
    this.lead_buyer = null;
    this.editLeadForm.patchValue({ buyer_detail_exist: false, buyer_contactid: null });
  }

  /**
   * Converts a lead to a contact by associating a contact ID or creating a new one.
   * Handles conflicts if the contact already exists and updates the lead contact.
   *
   * @param lead The lead object to be converted to a contact.
   * @param contact_id The contact ID to associate (defaults to null).
   * @param create_new Flag indicating whether to create a new contact (defaults to false).
   * @param is_clicked Flag indicating if the conversion was triggered by a click (defaults to false).
   * @param add_as_alternate Store the lead's email/mobile as the contact's alternates instead of overwriting its primary mobile.
   * @param alternate_label Label for that alternate entry; null when the agent left it empty.
   */
  convertToContact(
    lead,
    contact_id = null,
    create_new = false,
    is_clicked: boolean = false,
    add_as_alternate = false,
    alternate_label: string = null
  ) {
    this.contactConflict = false;
    if (lead?.contactid == '' || lead?.contactid == 'null' || lead?.contactid == null || lead?.hasOwnProperty('contactid') == false) {
      this.leads
        .convertLeadToContact(lead._id, {
          contactid: contact_id,
          createnew: create_new,
          add_as_alternate,
          alternate_label,
        })
        .subscribe({
          next: response => {
            if (response.hasOwnProperty('userexist') || response.hasOwnProperty('usersexist')) {
              this.contactList = response.userexist ?? response.usersexist;
              this.duplicateContactDraft = {
                firstName: lead?.first_name,
                lastName: lead?.last_name,
                fullName: lead?.full_name,
                email: lead?.email,
                mobile: lead?.mobile_phone,
              };
              this.contactConflict = true;
            } else {
              if (response.message == 'Email is required.') {
                this._toasterService.Error(response.message);
              } else {
                this.getLeadDetail();
                this._toasterService.Success(response.message);
              }
              this.lead_contact = response?.data;
              // The response carries the contact as it was before the link, so re-read it to pick
              // up the alternate entry the backend just added (REM-3497).
              this.getLeadContact(response?.data?._id ?? contact_id);
            }
            this.message.getModuleHistory(true);
            is_clicked ?? this.getLeadDetail();
          },
        });
    }
  }

  /**
   * Initiates a phone call by redirecting to the device's dialer with the provided number.
   *
   * @param number The phone number to dial.
   */
  dialCall(lead_detail) {
    let contactNumber = lead_detail?.mobile_phone ? lead_detail?.mobile_phone : lead_detail?.mobile_no;
    this.activityTracker.trackOnCurrentRecord('call', { entity_type: 'lead', entity_id: lead_detail?._id });
    window.location.href = 'tel:' + contactNumber;
  }

  /**
   * Opens a contact popup for editing or viewing the contact information of a lead.
   * Depending on the value of `mobToggle`, it either navigates to a contact page or adds a tab.
   *
   * @param lead The lead object containing contact information.
   */
  openContactPopup(isAgency: boolean = false, lead) {
    this.lmService.isEdit_show_div = 'edit_contact';
    if (isAgency == true) {
      let payload = {
        name: lead?.agency_name ? lead?.agency_name : lead?.name,
        _id: lead?.agencyid ? lead?.agencyid : lead?._id,
      };
      this.lmService.addTabInLevels('Contact - ' + payload?.name, payload?._id, payload, null, 'contact');
    } else {
      if (this.mobToggle == true) {
        this.router.navigate(['/setting/office-contact'], { queryParams: { id: lead?.contactid } });
      } else {
        let id = lead?.contactid ? lead?.contactid : lead?._id;
        let name = lead?.full_name ? lead?.full_name : lead?.name;
        if (id) {
          this.lmService.addTabInLevels('Contact - ' + name, id, lead, null, 'contact');
        } else {
          this.toaster.Error('Lead is not assigned to any contact');
        }
      }
    }
  }

  changeLeadStatus(event) {
    if (event?.tag_name === 'No Further Interest') {
      this.editLeadForm.patchValue({
        temperature: 'Cold',
      });
    }
    !event?.is_active ? (this.lead_status_type = 'Inactive') : (this.lead_status_type = 'Active');
    this.autofillStatusDate(event?.tag_name);
  }

  /** No Further Interest picked from the Activity timeline, waiting on its reason before saving. */
  pendingTimelineStatus: any = null;

  /**
   * Double-clicking a status in the Activity timeline changes the lead status in place.
   * It goes through the same changeLeadStatus() + silent save as the edit form, so the
   * No Further Interest reason rule and the REM-3537 No Response revert still apply.
   */
  onTimelineStatusDblClick(status: any): void {
    if (!status?.tag_name || status.tag_name === this.editLeadForm.controls['lead_status'].value) {
      return;
    }
    if (!this.isAllowEdit()) {
      this.toaster.Error('Do not have access to edit this lead');
      return;
    }
    if (status.tag_name === 'No Further Interest' && !this.editLeadForm.controls['not_interested'].value) {
      this.pendingTimelineStatus = status;
      return;
    }
    this.applyTimelineStatus(status);
  }

  onTimelineReasonChange(reason: any): void {
    if (!reason?.tag_name || !this.pendingTimelineStatus) {
      return;
    }
    const status = this.pendingTimelineStatus;
    this.pendingTimelineStatus = null;
    this.editLeadForm.patchValue({ not_interested: reason.tag_name });
    this.applyTimelineStatus(status);
  }

  cancelTimelineStatus(): void {
    this.pendingTimelineStatus = null;
  }

  private applyTimelineStatus(status: any): void {
    this.editLeadForm.patchValue({ lead_status: status.tag_name });
    this.changeLeadStatus(status);
    this.requestSilentSave();
  }

  // Lead Status -> the Lead Information date/time field(s) it stamps with the current date/time once that status is reached.
  // Agreement Executed has no corresponding field, so it intentionally stamps nothing here.
  private statusDateFieldMap: { [status: string]: { dateField: string; timeField?: string }[] } = {
    'Project Info Sent': [{ dateField: 'project_information_sent_date' }],
    'Agreement Sent': [{ dateField: 'agreement_sent_date' }],
    'Briefing Pending': [{ dateField: 'personal_briefing_date' }],
    'Briefing Completed': [{ dateField: 'personal_briefing_date' }, { dateField: 'onsite_training_date', timeField: 'onsite_training_time' }],
    'Trained and Engaged': [{ dateField: 'personal_briefing_date' }, { dateField: 'onsite_training_date', timeField: 'onsite_training_time' }],
  };

  autofillStatusDate(statusName: string) {
    if (!this.editLeadForm.controls['lead_type'].value?.includes('Agent')) return;
    const targets = this.statusDateFieldMap[statusName];
    if (!targets) return;
    const now = new Date();
    const patch: { [field: string]: any } = {};
    targets.forEach(({ dateField, timeField }) => {
      if (!this.editLeadForm.controls[dateField]?.value) patch[dateField] = now;
      if (timeField && !this.editLeadForm.controls[timeField]?.value) patch[timeField] = this.currentTimerSlot(now);
    });
    if (Object.keys(patch).length) this.editLeadForm.patchValue(patch);
  }

  // Rounds to the nearest slot in inspection_timer_options's format, e.g. '2pm' / '2:30pm'.
  private currentTimerSlot(now: Date): string {
    const hours = now.getHours();
    const roundedMinutes = now.getMinutes() < 30 ? 0 : 30;
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return roundedMinutes === 0 ? `${hour12}${period}` : `${hour12}:30${period}`;
  }

  selectNewDateField() {
    // Retrieve the 'created_at' and 'tenancy_end_date' values from the form
    let tenancy_end_date = this.editLeadForm.controls['tenancy_end_date'].value;
    let agreement_sent_date = this.editLeadForm.controls['agreement_sent_date'].value;
    let project_information_sent_date = this.editLeadForm.controls['project_information_sent_date'].value;
    let personal_briefing_date = this.editLeadForm.controls['personal_briefing_date'].value;
    let onsite_training_date = this.editLeadForm.controls['onsite_training_date'].value;

    let val2 = null;
    let val3 = null;
    let val4 = null;
    let val5 = null;
    let val6 = null;

    // If the 'tenancy_end_date' value exists, format it using the Utils.getDateStringFormat function
    if (tenancy_end_date) {
      val2 = Utils.getDateStringFormat(new Date(tenancy_end_date));
    }
    if (agreement_sent_date) {
      val3 = Utils.getDateStringFormat(new Date(agreement_sent_date));
    }
    if (project_information_sent_date) {
      val4 = Utils.getDateStringFormat(new Date(project_information_sent_date));
    }
    if (personal_briefing_date) {
      val5 = Utils.getDateStringFormat(new Date(personal_briefing_date));
    }
    if (onsite_training_date) {
      val6 = Utils.getDateStringFormat(new Date(onsite_training_date));
    }

    // Update the form values for 'created_at' and 'tenancy_end_date' with the formatted dates
    this.editLeadForm.patchValue({
      tenancy_end_date: val2 ? new Date(val2) : null,
      agreement_sent_date: val3 ? new Date(val3) : null,
      project_information_sent_date: val4 ? new Date(val4) : null,
      personal_briefing_date: val5 ? new Date(val5) : null,
      onsite_training_date: val6 ? new Date(val6) : null,
    });
  }

  selectDate() {
    let date_value = this.editLeadForm.controls['inspection_dateTime'].value;
    let val = Utils.getDateStringFormat(date_value);
    this.editLeadForm.patchValue({
      inspection_dateTime: new Date(val),
      temperature: 'Top Prospect',
    });
  }

  // ---------- Auto-save ----------

  /** Idle time after the last edit before the lead is auto-saved. */
  private readonly AUTO_SAVE_DEBOUNCE_MS = 1500;
  /** Toolbar indicator state, shown only while in edit mode. */
  autoSaveState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  autoSaveError: string | null = null;
  /** Serialized form value of the last successful save — auto-save is skipped while it still matches. */
  private lastSavedSnapshot: string | null = null;
  /** True from the moment an update-lead request goes out until it settles (any save path). */
  private saveInFlight = false;
  /** A change landed while a save was in flight; re-run once that save settles. */
  private autoSavePending = false;
  /** At least one auto-save succeeded, so grid/history listeners need one refresh when edit mode closes. */
  private autoSaveDirtyForListeners = false;
  /** Last auto-save error already toasted — repeats are shown inline only, so typing isn't spammed. */
  private lastAutoSaveErrorToast: string | null = null;
  private readonly autoSaveTick$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  /**
   * Auto-save: every change made in edit mode is persisted after a short idle pause, so a
   * closed tab or lost session never loses typed data. The manual Save / Save & Close
   * buttons keep their existing behaviour (toast, grid refresh, lead reload).
   */
  private setupAutoSave(): void {
    this.editLeadForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.scheduleAutoSave());

    this.autoSaveTick$.pipe(debounceTime(this.AUTO_SAVE_DEBOUNCE_MS), takeUntil(this.destroy$)).subscribe(() => this.runAutoSave());

    // The contact-attempt note popup is reachable from view mode, where the edit-session
    // auto-save never runs — give its draft its own debounced save.
    this.contactNoteSaveTick$.pipe(debounceTime(this.AUTO_SAVE_DEBOUNCE_MS), takeUntil(this.destroy$)).subscribe(() => this.saveContactNoteDraft());
  }

  /** Queue an auto-save. Also called directly for edits that live outside the form (tags, inspection time). */
  scheduleAutoSave(): void {
    if (this.canAutoSave()) {
      this.autoSaveTick$.next();
    }
  }

  /** Auto-save only runs on a fully loaded lead the user is actively — and legitimately — editing. */
  private canAutoSave(): boolean {
    return (
      this.autoSaveEnabled &&
      this.isLeadEdit &&
      this.isLeadDetailLoaded &&
      !!this.lead_detail?._id &&
      !this.leadDetailFetchInFlight &&
      this.isAllowEdit()
    );
  }

  /**
   * Serialized picture of everything onSaveLeadDetail sends. Tags (`todo`) and the inspection
   * time dropdown (`inspectionTime`) are plain fields rather than form controls, so they are
   * folded in here. Returns null when the value can't be serialized — treated as "changed"
   * rather than "unchanged", so a save is never silently dropped.
   */
  private snapshotForm(): string | null {
    try {
      return JSON.stringify({
        form: this.editLeadForm?.getRawValue() ?? {},
        tag: this.todo ?? [],
        inspectionTime: this.inspectionTime ?? null,
      });
    } catch {
      return null;
    }
  }

  /** Fires the debounced save, unless nothing changed or a save is already in flight. */
  private runAutoSave(): void {
    if (!this.canAutoSave()) {
      return;
    }
    const snapshot = this.snapshotForm();
    if (snapshot !== null && snapshot === this.lastSavedSnapshot) {
      return;
    }
    if (this.saveInFlight) {
      // Never overlap update-lead requests — the later one could land out of order.
      this.autoSavePending = true;
      return;
    }
    this.onSaveLeadDetail(false, true, { silent: true, skipLeadReload: true, preserveIsLeadEdit: true, auto: true });
  }

  /** Runs the save that was deferred while another one was in flight. */
  private drainPendingAutoSave(): void {
    if (!this.autoSavePending) {
      return;
    }
    this.autoSavePending = false;
    setTimeout(() => {
      if (this.saveInFlight) {
        this.autoSavePending = true;
        return;
      }
      const snapshot = this.snapshotForm();
      if (snapshot !== null && snapshot === this.lastSavedSnapshot) {
        return;
      }
      this.onSaveLeadDetail(false, true, { silent: true, skipLeadReload: true, preserveIsLeadEdit: true, auto: true });
    });
  }

  /**
   * Silent background save used by the auto-summary / contact-attempt paths. Defers rather than
   * overlapping an in-flight request; drainPendingAutoSave() picks the change back up.
   */
  private requestSilentSave(saveOptions?: { fromSummaryPersist?: boolean }): void {
    if (this.saveInFlight) {
      this.autoSavePending = true;
      return;
    }
    this.onSaveLeadDetail(false, true, { silent: true, skipLeadReload: true, preserveIsLeadEdit: true, auto: true, ...saveOptions });
  }

  /**
   * Auto-saves stay quiet while typing (no grid refresh, no history reload, no lead refetch).
   * Once the user leaves edit mode, fire those refresh signals once for everything auto-save persisted.
   */
  private notifyListenersAfterAutoSave(): void {
    if (!this.autoSaveDirtyForListeners) {
      return;
    }
    this.autoSaveDirtyForListeners = false;
    this.lmService.updateProjectSummary(true);
    this.lmService.isLeadUpdate(true);
    this.message.getModuleHistory(true);
    this.isSavedClosed.emit({ isClose: false, lead: null });
  }

  /**
   * Saves the lead details after updating the form values.
   * It also manages lead status and triggers actions based on whether the lead is saved and closed.
   *
   * @param save_and_close Flag indicating whether to save and close the lead (defaults to false).
   * @param extra Additional flag for extra handling (defaults to false).
   * @param saveOptions Optional: silent suppresses success toast; skipLeadReload avoids refetch and skips inspection-summary patch (e.g. auto backfill on load); preserveIsLeadEdit leaves edit/view toggle unchanged after save (e.g. auto summary persist while editing); auto marks a background auto-save, which reports through the toolbar indicator instead of toasts and defers grid/history refresh until edit mode closes; fromSummaryPersist marks the follow-up save that writes a freshly generated AI summary, so it doesn't ask for another one.
   */
  onSaveLeadDetail(
    save_and_close: boolean = false,
    extra: boolean = false,
    saveOptions?: { silent?: boolean; skipLeadReload?: boolean; preserveIsLeadEdit?: boolean; auto?: boolean; fromSummaryPersist?: boolean }
  ) {
    if (!this.isLeadDetailLoaded || !this.lead_detail?._id) {
      return;
    }
    const isAuto = saveOptions?.auto === true;
    if (isAuto) {
      this.autoSaveState = 'saving';
      this.autoSaveError = null;
    }
    // manageLeadStatus() stamps the new status into the log in place, so keep what was there in case
    // the backend refuses the move (REM-3537 No Response gate) and the picker has to go back.
    const statusBeforeSave = this.captureLeadStatusState();
    this.manageLeadStatus(false);
    this.syncInspectionStatus();

    let formData = this.editLeadForm.value;
    formData['application_submitted_date'] = formData['application_submitted'] == true ? new Date() : null;
    formData['inspection_dateTime'] = formData['inspection_dateTime']
      ? typeof formData['inspection_dateTime'] === 'string'
        ? formData['inspection_dateTime']
        : moment(formData['inspection_dateTime']).format('YYYY-MM-DD') + 'T' + this.inspectionTime
      : null;
    // formData['inspection_dateTime'] = formData['inspection_dateTime']
    //   ? moment(formData['inspection_dateTime']).format('YYYY-MM-DD') + 'T' + this.inspectionTime
    //   : null;
    formData['personal_briefing_date'] = formData['personal_briefing_date']
      ? moment(formData['personal_briefing_date']).format('YYYY-MM-DD') + 'T' + this.formatTime(formData['personal_briefing_time'])
      : null;
    formData['onsite_training_date'] = formData['onsite_training_date']
      ? moment(formData['onsite_training_date']).format('YYYY-MM-DD') + 'T' + this.formatTime(formData['onsite_training_time'])
      : null;
    formData['agreement_sent_date'] = formData['agreement_sent_date'] ? moment(formData['agreement_sent_date']).format('YYYY-MM-DD') : null;
    formData['project_information_sent_date'] = formData['project_information_sent_date']
      ? moment(formData['project_information_sent_date']).format('YYYY-MM-DD')
      : null;
    formData['postcode'] = normalisePostcode(formData['postcode']);
    formData['is_read'] = true;
    formData['status_type'] = !this.editLeadForm.controls['lead_status'].value ? 'Inactive' : this.lead_status_type;
    formData['tag'] = this.todo;
    formData['latest_inspection_status'] = formData['latest_inspection_status'] || null;
    formData['isOwnerTeam'] = this.isOwnerTeam;
    formData['isAgentResponsibleTeam'] = this.isAgentResponsibleTeam;
    // Buyer details only belong to Agent leads; a lead switched to another type drops them.
    if (formData['lead_type'] !== 'Agent' || !formData['buyer_detail_exist']) {
      formData['buyer_detail_exist'] = false;
      formData['buyer_contactid'] = null;
    }

    delete formData['personal_briefing_time'];
    delete formData['onsite_training_time'];

    // Taken after manageLeadStatus/syncInspectionStatus have finished patching the form, so the
    // valueChanges they emit compare equal once the save lands and don't re-trigger auto-save.
    const snapshotAtSend = this.snapshotForm();
    this.saveInFlight = true;

    this.leads
      .updateLead(this.lead_detail?._id, formData)
      .pipe(
        finalize(() => {
          this.saveInFlight = false;
          this.drainPendingAutoSave();
        })
      )
      .subscribe({
        next: response => {
          const { status } = response;
          if (status) {
            this.lastSavedSnapshot = snapshotAtSend;
            this.lastAutoSaveErrorToast = null;
            this.closeSuggestionRefreshKey++;
            if (saveOptions?.skipLeadReload) {
              this.applyStoredReportNotes(response?.data, formData['report_notes'], snapshotAtSend);
            }
            if (isAuto) {
              this.autoSaveState = 'saved';
              this.autoSaveDirtyForListeners = true;
              // Auto-save skips the lead refetch, so keep the cached copy in step — otherwise
              // manageLeadStatus() sees a stale lead_status and re-stamps changed_on every cycle.
              this.lead_detail = { ...this.lead_detail, lead_status: this.editLeadForm.controls['lead_status']?.value };
              // Background saves outside an edit session have no "edit mode closed" moment
              // to batch against, so refresh the listeners straight away.
              if (!this.isLeadEdit) {
                this.notifyListenersAfterAutoSave();
              }
            }
            if (!saveOptions?.silent) {
              this._toasterService.Success('Lead updated successfully');
            }
            // Lead edit triggers a fresh Dynamics push — flip an already-synced badge back to pending optimistically.
            if (this.lead_detail?.dynamic_sync_status === 'synced') {
              this.lead_detail = { ...this.lead_detail, dynamic_sync_status: 'pending', dynamic_sync_error: null };
              this.startDynamicSyncPolling();
            }
            // Auto-save keeps quiet while the user is still typing — these refresh signals are
            // fired once by notifyListenersAfterAutoSave() when edit mode closes.
            if (!isAuto) {
              this.lmService.updateProjectSummary(true);
              this.lmService.isLeadUpdate(true);
              this.isSavedClosed.emit({ isClose: false, lead: null });
            }
            if (!save_and_close && !extra) {
              this.isLeadClose.emit(true);
            }
            if (save_and_close) {
              setTimeout(async () => {
                let tab = this.lmService.tab_levels.find(el => el.name.includes('Lead'));
                if (tab) {
                  let ind = this.lmService.tab_levels.indexOf(tab);
                  this.isLeadClose.emit({ level: tab, index: ind });
                } else {
                  this.isLeadClose.emit({ level: null, index: 0 });
                }
                this.isLeadEdit = false;
                await this.updatedLead.emit(true);
                this._workflowService.isWorkflowProcessTabOpened == true && this.GetListingWorkflowProcesses();
              }, 1000);
            }
            if (!saveOptions?.skipLeadReload) {
              this.getLeadDetail();
            }
            if (!isAuto) {
              this.message.getModuleHistory(true);
            }
            const words: string[] = this.editLeadForm.controls['lead_type']?.value?.split(' ') || [];
            const firstLetters = words.map(word => word.charAt(0));
            let tab_title_array = {
              lead: 'Lead',
              lead_type: firstLetters.join(''),
              name:
                this.editLeadForm.controls['first_name']?.value && this.editLeadForm.controls['last_name']?.value
                  ? this.editLeadForm.controls['first_name']?.value + ' ' + this.editLeadForm.controls['last_name']?.value
                  : this.editLeadForm.controls['full_name']?.value,
            };
            // Auto-save can land after the tab was closed; only touch the entry if it's still there.
            if (this.lmService.tab_levels?.[this.tabIndex]) {
              this.lmService.tab_levels[this.tabIndex].extra = tab_title_array;
            }
            if (!saveOptions?.fromSummaryPersist) {
              this.refreshAiLeadSummaryAfterSave();
            }
          } else {
            this.handleUpdateLeadFailure(response, isAuto, statusBeforeSave);
          }
        },
        error: error => {
          this.handleUpdateLeadFailure(error?.error || error, isAuto, statusBeforeSave);
        },
      });
  }

  /**
   * Handles validation/business errors from update-lead (HTTP 200 with status:false or error response).
   * Shows the API message, highlights invalid fields, switches to edit mode, and scrolls to the first error.
   *
   * @param isAuto Failure came from a background auto-save: highlight the fields but never steal
   *               scroll position mid-typing, and toast each distinct problem only once.
   */
  private handleUpdateLeadFailure(
    apiError: { message?: string; fields?: string[]; data?: any },
    isAuto = false,
    statusBeforeSave: LeadStatusState = null
  ): void {
    if (apiError?.data?.contact_requirement) {
      this.handleNoResponseBlocked(apiError.data.contact_requirement, isAuto, statusBeforeSave);
      return;
    }
    const fields = apiError?.fields;
    if (fields?.length) {
      this.isLeadEdit = true;
      this.invalidFields = fields;
      // Allow edit template + error classes to render before querying the DOM.
      if (!isAuto) {
        this.focusInvalidFields(100);
      }
    } else {
      this.invalidFields = [];
    }

    const errorArray = ['Not Interested  required', 'No Further Interest required'];
    const errorMessage = errorArray.includes(apiError?.message)
      ? 'Please select a reason for no further interest before saving.'
      : apiError?.message || 'Something went wrong';

    if (isAuto) {
      // The toolbar indicator carries the state; toast only when the problem changes so a
      // half-filled form doesn't fire the same error every debounce cycle.
      this.autoSaveState = 'error';
      this.autoSaveError = errorMessage;
      if (this.lastAutoSaveErrorToast !== errorMessage) {
        this.lastAutoSaveErrorToast = errorMessage;
        this._toasterService.Error(errorMessage);
      }
      return;
    }

    // Always toast validation errors — silent only suppresses success toasts on auto-save.
    this.lastAutoSaveErrorToast = null;
    this._toasterService.Error(errorMessage);
  }

  private captureLeadStatusState(): LeadStatusState {
    const log = this.editLeadForm.controls['lead_status_log']?.value;
    return {
      status: this.lead_detail?.lead_status ?? null,
      statusType: this.lead_detail?.status_type ?? this.lead_status_type,
      log: Array.isArray(log) ? log.map(entry => ({ ...entry })) : null,
      timeline: (this.leadStatus || []).map(entry => ({ ...entry })),
    };
  }

  /** Bumped after every save so the close-suggestion banner re-asks — a save can log the attempt that tips it. */
  closeSuggestionRefreshKey = 0;

  /** The lead was closed as No Further Interest from Remmi's banner — show what the backend stored. */
  onLeadClosedFromSuggestion(): void {
    this.leads.clearLeadByIdCache(this.lead_detail?._id);
    this.getLeadDetail();
    this.getTasks();
  }

  /**
   * Re-reads the lead after something outside the form changed it on the server (a follow-up task
   * moves `follow_up_date` / `has_open_follow_up`). Skipped while the form holds unsaved edits, which a
   * reload would overwrite — auto-save brings the lead back in step once they are sent.
   */
  private reloadLeadIfSaved(): void {
    const snapshot = this.snapshotForm();
    if (snapshot !== null && snapshot === this.lastSavedSnapshot) {
      // The change came through another service, so a lead response shared from just before it is stale.
      this.leads.clearLeadByIdCache(this.lead_detail?._id);
      this.getLeadDetail();
    }
  }

  /**
   * The backend rewrites shorthand note lines on save ("gp - vm, sms and email sent" →
   * "GP - Voicemail, SMS and Email", REM-3537_2026-10-13 §1), so after a save the notes on screen may
   * not be what is stored. Saves that reload the lead pick it up anyway; for the ones that don't
   * (auto-save), the stored text is taken from the response, or fetched when the response has none.
   *
   * Only applied while the form is still exactly what was sent — if the agent has typed on, their
   * text wins and the next save is rewritten in turn.
   */
  private applyStoredReportNotes(savedLead: any, sentNotes: string, snapshotAtSend: string | null): void {
    const sent = sentNotes || '';
    if (sent === this.confirmedReportNotes) {
      return;
    }
    const apply = (stored: string) => {
      if (snapshotAtSend === null || this.snapshotForm() !== snapshotAtSend) {
        return;
      }
      this.confirmedReportNotes = stored;
      if (stored === sent) {
        return;
      }
      this.editLeadForm.controls['report_notes'].setValue(stored, { emitEvent: false });
      this.lead_detail = { ...this.lead_detail, report_notes: stored };
      this.lastSavedSnapshot = this.snapshotForm();
    };
    if (typeof savedLead?.report_notes === 'string') {
      apply(savedLead.report_notes);
      return;
    }
    const leadId = this.lead_detail?._id;
    this.leads
      .getLeadById(leadId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (leadId === this.lead_detail?._id && typeof response?.data?.report_notes === 'string') {
            apply(response.data.report_notes);
          }
        },
      });
  }

  /** The notes as last confirmed by the server — a save that didn't touch them needs no re-read. */
  private confirmedReportNotes = '';

  /** REM-3537 "Not ready for No Response yet" box — what the refused save was missing. */
  showNoResponseBlock: boolean = false;
  noResponseBlock: { blockedStatus: string; keptStatus: string; requirement: ContactRequirement } = null;
  isNoResponseFollowUpSaving: boolean = false;
  noResponseBookedFollowUp: string = null;

  /**
   * What the box's Follow-up tile shows as booked: the lead's open follow-up task (the one in the
   * FOLLOW UP box beside the form), whoever created it — so reopening the box after booking, or after
   * using the flag menu, never offers to add a second one. The label set on booking only bridges the
   * moment before the task list has reloaded.
   */
  get noResponseFollowUpLabel(): string {
    const task = this.followUpTask;
    if (task?.due_date) {
      return `${moment(task.due_date).format('ddd D MMM')}${task.task_time ? ', ' + task.task_time : ''}`;
    }
    return this.noResponseBookedFollowUp;
  }

  /** A follow-up task needs the office's Follow Up type, an open status and a priority to be created. */
  get canAddNoResponseFollowUp(): boolean {
    return !!this.jobTypes && !!this.taskStatuses && !!this.priorities;
  }

  /**
   * Books the follow-up the No Response box asked for, without leaving it — the same task the lead's
   * Follow Up menu creates. Booking is only half the rule; the box says it still has to be completed.
   */
  addNoResponseFollowUp(choice: NoResponseFollowUpChoice): void {
    if (!this.lead_detail || !this.canAddNoResponseFollowUp || this.isNoResponseFollowUpSaving) {
      return;
    }
    const dueDate = choice.selection ? choice.selection.dueDate : new Date(choice.date);
    const taskTime = choice.selection ? choice.selection.taskTime : choice.time;
    this.isNoResponseFollowUpSaving = true;
    this.taskService.addTask(this.followUpTaskPayload(dueDate, taskTime)).subscribe({
      next: () => {
        this.isNoResponseFollowUpSaving = false;
        this.noResponseBookedFollowUp = choice.label;
        this.toaster.Success('Follow up added successfully');
        this.getTasks();
        this.reloadLeadIfSaved();
      },
      error: error => {
        this.isNoResponseFollowUpSaving = false;
        this.toaster.Error(error?.message || 'Could not add the follow up. Please try again.');
      },
    });
  }

  /**
   * REM-3537: the backend refuses a move to No Response until the lead has had its required contact touchpoints (9, or 3 for a Reassigned lead)
   * and a completed follow-up in one 14-day window (HTTP 422 with `data.contact_requirement`).
   *
   * The picker goes back to the saved status rather than showing one that never persisted, and the
   * reason is shown in a box instead of a toast — the client found the long backend sentence, stacked
   * once per auto-save attempt, unreadable. The restore is silent: with events on, auto-save would
   * re-send straight away and, if the backend refused again, toast in a loop. Any other edits the
   * refused save carried are re-sent once instead.
   */
  private handleNoResponseBlocked(requirement: ContactRequirement, isAuto: boolean, state: LeadStatusState): void {
    const blockedStatus = this.editLeadForm.controls['lead_status']?.value ?? null;
    const isReverted = !!state && blockedStatus !== state.status;
    if (isReverted) {
      this.leadStatus = state.timeline;
      this.lead_status_type = state.statusType;
      this.editLeadForm.patchValue({ lead_status: state.status, lead_status_log: state.log }, { emitEvent: false });
      if (this.canAutoSave()) {
        this.autoSavePending = true;
      }
    }
    this.invalidFields = [];
    this.lastAutoSaveErrorToast = null;
    if (isAuto) {
      this.autoSaveState = isReverted ? 'idle' : 'error';
      this.autoSaveError = isReverted ? null : 'Contact requirements for No Response not met';
    }
    if (!this.showNoResponseBlock) {
      this.noResponseBookedFollowUp = null;
    }
    // Loaded up front so the box can book a follow-up the moment it opens.
    if (!this.canAddNoResponseFollowUp) {
      this.getTaskStatusTypes();
    }
    this.noResponseBlock = {
      blockedStatus: isReverted ? blockedStatus : (state?.status ?? blockedStatus),
      keptStatus: isReverted ? state.status : null,
      requirement,
    };
    this.showNoResponseBlock = true;
  }

  /**
   * The header shows the postcode beside the address, but many leads carry it inside the address
   * already ("… QLD 4101", or the address is just "4101"), and it then read twice. Only shown on
   * its own when the address does not already contain it.
   */
  get showHeaderPostcode(): boolean {
    const postcode = normalisePostcode(this.lead_detail?.postcode);
    if (!postcode) {
      return false;
    }
    const address = String(this.lead_detail?.contact_address || '').toLowerCase();
    return !address.split(/[^a-z0-9]+/).includes(postcode.toLowerCase());
  }

  invalidFields: string[] = [];
  isFieldInvalid(fieldName: string): boolean {
    return this.invalidFields?.includes(fieldName);
  }

  // p-inputSwitch pulls focus onto its own checkbox when toggled, which blurs the
  // ng-select search box in the dropdown header. Hand focus back after the toggle.
  refocusSelectSearch(select: NgSelectComponent): void {
    setTimeout(() => select?.searchInput?.nativeElement?.focus());
  }

  // Selector matching every error-highlight class used across the lead detail form.
  private readonly invalidFieldSelector = '._input-error, .invalidField, .is-invalid, .invalidCalendarField';

  /**
   * After the update API reports required/invalid fields, open any collapse box that
   * holds a highlighted field and scroll the first invalid field into view.
   */
  /** Opens the INSPECTION box and scrolls it into view, once the overview has rendered. */
  private focusInspectionBox(): void {
    setTimeout(() => {
      const boxes = this.collapseBoxes?.toArray() || [];
      const boxEls = this.collapseBoxEls?.toArray() || [];
      const index = boxEls.findIndex(ref => (ref?.nativeElement as HTMLElement)?.getAttribute('data-help-target') === 'lead-detail-inspection');
      if (index < 0) {
        return;
      }
      boxes[index].isCollapsed = false;
      setTimeout(() => (boxEls[index].nativeElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }, 300);
  }

  private focusInvalidFields(renderDelayMs = 0): void {
    if (!this.invalidFields?.length) return;

    // Wait for edit template + ngClass error styles before querying the DOM.
    setTimeout(() => {
      const invalidEls = Array.from((this.hostEl.nativeElement as HTMLElement).querySelectorAll(this.invalidFieldSelector)) as HTMLElement[];
      if (!invalidEls.length) return;

      // Open any collapsed box that contains an invalid field.
      const boxes = this.collapseBoxes?.toArray() || [];
      const boxEls = this.collapseBoxEls?.toArray() || [];
      boxes.forEach((box, i) => {
        const boxEl = boxEls[i]?.nativeElement as HTMLElement;
        if (box?.isCollapsed && boxEl && invalidEls.some(field => boxEl.contains(field))) {
          box.isCollapsed = false;
        }
      });

      // Scroll to the first invalid field once the boxes are expanded.
      setTimeout(() => invalidEls[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }, renderDelayMs);
  }

  formatTime(time: string): string {
    if (!time) return '';

    // agar already : ho to skip kar do
    if (time.includes(':')) return time;

    // 8am -> 8:00am
    return time.replace(/(am|pm)/i, ':00$1');
  }

  /**
   * Handles actions triggered by the collapse/expand events of UI elements.
   * It updates the visibility state of the corresponding form sections or triggers specific actions.
   * @param actionName The name of the action (e.g., 'opened-lead', 'closed-Notes').
   */
  CollapseBoxActionTriggered(actionName: string) {
    if (!actionName) return;
    if (actionName.startsWith('opened-') || actionName.startsWith('closed-')) {
      const [actionType, actionTarget] = actionName.split('-');
      const isOpened = actionType === 'opened';

      const formSettingsMap: { [key: string]: keyof typeof this.defaultFormSetting } = {
        lead: 'enquiry_overview',
        Notes: 'notes',
        Tasks: 'tasks',
        Appointment: 'appointments',
        Requirements: 'requirement',
        Email: 'email',
        'Lead Details': 'enquiry_detail',
        Tags: 'tags',
      };

      if (formSettingsMap[actionTarget]) {
        this.defaultFormSetting = { ...this.defaultFormSetting, [formSettingsMap[actionTarget]]: !isOpened };
      }
    } else {
      const actionMap: { [key: string]: () => void } = {
        Tags: () => {
          this.lmService.tag_manager = !this.lmService.tag_manager;
        },
        Tasks: () => {
          this.addNewTask(null, true);
        },
        Notes: () => {
          this.addNotesTrigger.next(true);
        },
        Appointment: () => {
          this.tabIndexValue = 1;
        },
      };

      actionMap[actionName]?.();
    }
  }

  /**
   * Adds a new task or opens the task in edit mode based on the value of `new_or_edit`.
   * If editing an existing task, it navigates to the task page or opens a tab depending on the device's mode (mobile or desktop).
   *
   * @param task The task object to be edited.
   * @param new_or_edit Flag to determine if the task is new (true) or to edit an existing one (false).
   */
  addNewTask(task, new_or_edit) {
    this.selected_task = task;
    this.lmService.is_new_task = true;
    if (new_or_edit == true) {
      this.lmService.addTabInLevels('Task', 'createnew');
    } else {
      if (this.mobToggle == true) {
        this.router.navigate(['/tasks/all-tasks'], { queryParams: { id: task?._id } });
      } else {
        const taskId = task?.id ? `Task: REM-${task.id}` : 'Task';
        this.lmService.addTabInLevels(`${taskId}`, task?._id, task);
      }
    }
  }

  /**
   * Toggles the open/closed state of a box (such as a collapsible section).
   *
   * @param box The box object whose state is being toggled.
   */
  toggleBox(box): void {
    box.isOpen = !box.isOpen;
  }

  /**
   * Filters the tags based on the `keyword_tags` entered by the user.
   * The filter operation is case-insensitive and is applied to each tag's `tag_name`.
   */
  filterItem() {
    this.tagsList = this.tagsDataCopy;
    let value = this.keyword_tags?.toLowerCase();
    this.tagsList = this.tagsList?.map(ele => {
      return {
        ...ele,
        types: ele?.types?.map(el => {
          return { ...el, data: el?.data?.filter(x => x?.tag_name?.toLowerCase().includes(value)) };
        }),
      };
    });
  }

  /**
   * Fetches the tags for leads from the API and processes them.
   * It maps over the data, ensuring that each tag type is initialized as open,
   * and then allocates the tags to various lead fields.
   */
  lead_tag_module_id: any = null;
  getLeadTags() {
    this.redux.getTags(this.api.getOfficeId(), true, 'lead').subscribe({
      next: response => {
        const { status, data } = response;
        if (status) {
          let res_data = JSON.parse(JSON.stringify(data))?.map(ele => {
            return {
              ...ele,
              types: ele?.types?.map(el => {
                return (el = { ...el, isOpen: true });
              }),
            };
          });
          this.lead_tag_module_id = res_data[0]?._id;
          this.tagsDataCopy = this.tagsList = res_data;
          this.getLeadDetail();
          this.allocateTagsToFields(null);
          this.allocateTagsToFields('lead-type');
          this.allocateTagsToFields('status-type');
          this.allocateTagsToFields('lead-source');
          this.allocateTagsToFields('property-type');
          this.allocateTagsToFields('lead-status');
          this.allocateTagsToFields('new-or-established-property');
          this.allocateTagsToFields('current-purpose');
          this.allocateTagsToFields('tenancy-detail');
          this.allocateTagsToFields('reason-for-buying');
          this.allocateTagsToFields('reason-for-selling');
          this.allocateTagsToFields('timeframe');
          this.allocateTagsToFields('price-range');
          this.allocateTagsToFields('intended-use');
          this.allocateTagsToFields('outdoor-feature');
          this.allocateTagsToFields('indoor-feature');
          this.allocateTagsToFields('development-type');
          this.allocateTagsToFields('lead-status-no-further-interest');
          this.allocateTagsToFields('project-status');
          this.allocateTagsToFields('temperature');
          this.allocateTagsToFields('agreement');
          this.allocateTagsToFields('enquiry-type');
          this.manageLeadStatus(true);
        } else {
          this.tagsList = [];
          this.getLeadDetail();
        }
      },
      error: () => {
        this.tagsList = [];
        this.getLeadDetail();
      },
    });
  }

  leadStatusIcon: any = [
    { tag_name: 'Reassigned (Old Lead)', icon: 'icon-refresh' },
    { tag_name: 'Purchased', icon: 'pi pi-check' },
    { tag_name: 'No Response (>28 Days)', icon: 'pi pi-clock' },
    { tag_name: 'No Response (14 to 28 Days)', icon: 'pi pi-clock' },
    { tag_name: 'Contact Attempted', icon: 'pi pi-phone' },
    { tag_name: 'Call Answered', icon: 'pi pi-phone' },
    { tag_name: 'New', icon: 'pi pi-user' },
    { tag_name: 'Nurturing (Post Appointment)', icon: 'pi pi-calendar' },
    { tag_name: 'Nurturing (Pre Appointment)', icon: 'pi pi-calendar' },
    { tag_name: 'No Further Interest', icon: 'pi pi-thumbs-down' },
    { tag_name: 'Waiting Future Stage', icon: 'pi pi-box' },
  ];

  applyColorCode(temperature) {
    switch (temperature) {
      case 'Warm':
        return '#FFBF00';
      case 'Top Prospect':
        return '#cd1818';
      case 'Cold':
        return '#6bd4e4';
      default:
        return '#6bd4e4';
    }
  }

  not_interested_reason: any = [];
  agreementList: any = [];
  allocateTagsToFields(
    choice:
      | 'status-type'
      | 'developer'
      | 'commercial'
      | 'project-status'
      | 'development-type'
      | 'new-or-established-property'
      | 'current-purpose'
      | 'tenancy-detail'
      | 'reason-for-buying'
      | 'reason-for-selling'
      | 'timeframe'
      | 'price-range'
      | 'intended-use'
      | 'outdoor-feature'
      | 'indoor-feature'
      | 'lead-status'
      | 'lead-source'
      | 'lead-type'
      | 'lead-status-no-further-interest'
      | 'property-type'
      | 'enquiry-type'
      | 'agreement'
      | 'temperature' = null
  ) {
    let removeT: any[] = this.tagsList?.[0]?.types || [];
    let availableTags: any[] = removeT.map(ele => {
      return { ...ele, slug: ele?.slug?.toLowerCase()?.includes('\t') ? ele?.slug?.toLowerCase().replace('\t', '') : ele?.slug };
    });

    switch (choice) {
      case 'lead-status': {
        // Prefer a lead-type-specific status list (e.g. 'agent-lead-status') if one exists, otherwise fall back to the generic list
        let lead_type_slug: string =
          this.leadTypes?.find(tag => tag?.tag_name?.toLowerCase() === this.editLeadForm.controls?.[`lead_type`]?.value?.toLowerCase())?.tag_slug ||
          '';
        let foundedData =
          (lead_type_slug && availableTags?.find(local_tag => local_tag?.slug?.toLowerCase() === `${lead_type_slug}-lead-status`?.toLowerCase())) ||
          availableTags?.find(local_tag => local_tag?.slug?.toLowerCase() === choice?.toLowerCase());

        let leadStatus =
          foundedData && Object.keys(foundedData)?.length > 0 ? [...foundedData?.data]?.sort((a, b) => (a?.tag_order > b?.tag_order ? 1 : -1)) : null;
        const merged = leadStatus.map(item => {
          const match = this.leadStatusIcon?.find(x => x.tag_name === item.tag_name);
          return {
            ...item,
            icon: match ? match.icon : null,
          };
        });

        this.leadStatus = merged;

        console.log('this.leadStatus ??:', this.leadStatus);
        break;
      }
      case 'lead-source': {
        if (this.typeName === 'prospective-seller') {
          let lead_type_slug: string =
            this.leadTypes?.find(tag => tag?.tag_name?.toLowerCase() === this.editLeadForm.controls?.[`lead_type`]?.value?.toLowerCase())?.tag_slug ||
            '';
          let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === `${lead_type_slug}-lead-source`?.toLowerCase());
          if (finedData) {
            this.leadSource =
              finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
          }
        } else {
          let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
          this.leadSource =
            finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        }
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.leadSource =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'status-type': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.status_type =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'enquiry-type': {
        // Find the data based on the 'choice' and sort by tag_name
        let foundedData = availableTags?.find(local_tag => local_tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.enquiry_Type =
          foundedData && Object.keys(foundedData)?.length > 0 ? [...foundedData?.data].sort((a, b) => a?.tag_name.localeCompare(b?.tag_name)) : null;
        break;
      }
      case 'agreement': {
        // Fixed order (not alphabetical) — matches the agreement lifecycle: sent, returned signed, then executed copy sent back
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        let order = ['Agreement Sent', 'Signed Agreement Returned', 'Executed Agreement Sent'];
        this.agreementList =
          finedData && Object.keys(finedData)?.length > 0
            ? finedData?.data?.sort((a, b) => order.indexOf(a?.tag_name) - order.indexOf(b?.tag_name))
            : null;
        break;
      }
      case 'lead-status-no-further-interest': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.not_interested_reason =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'lead-type': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.leadTypes =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'property-type': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.listingTypes =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'current-purpose': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.current_purpose =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'tenancy-detail': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.tenancy_details =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'reason-for-buying': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.reason_for_buying =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'reason-for-selling': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.reason_for_selling =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'timeframe': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.time_frame =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        const customOrder = ['Immediate', '3 Months', '6 Months', '12 Months', 'Unknown'];

        this.time_frame =
          finedData && Object.keys(finedData)?.length > 0
            ? finedData?.data?.sort((a, b) => customOrder.indexOf(a?.tag_name) - customOrder.indexOf(b?.tag_name))
            : null;
        break;
      }

      case 'price-range': {
        let foundedData = availableTags?.find(local_tag => local_tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.budgetList = foundedData && Object.keys(foundedData)?.length > 0 ? Leads.sortPriceRange(foundedData?.data) : null;
        break;
      }

      case 'intended-use': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.intended_use =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'outdoor-feature': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.outdoor_feature =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'indoor-feature': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.indoor_feature =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'development-type': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.developmentType =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'project-status': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.projectStatus =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'new-or-established-property': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.Established_property =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;
        break;
      }
      case 'temperature': {
        let finedData = availableTags?.find(tag => tag?.slug?.toLowerCase() === choice?.toLowerCase());
        this.temperature =
          finedData && Object.keys(finedData)?.length > 0 ? finedData?.data?.sort((a, b) => (a?.tag_name > b?.tag_name ? 1 : -1)) : null;

        const order = ['Top Prospect', 'Warm', 'Cold'];
        this.temperature = this.temperature.sort((a, b) => {
          return order.indexOf(a.tag_name) - order.indexOf(b.tag_name);
        });
        break;
      }

      default:
        break;
    }
  }

  /**
   * Duplicates the selected lead and creates a new lead.
   * It sends the lead data to the backend and displays success or error messages.
   */
  duplicateRecords() {
    let dup_array = [this.lead_detail?._id];
    this.leads.duplicateLeads({ lead_ids: dup_array, lead_type: this.dup_lead_type }).subscribe({
      next: () => {
        this._toasterService.Success('Lead created');
        this.getLeadDetail();
        this.dup_lead_type = null;
      },
      error: () => {
        this._toasterService.Error('Something has gone wrong');
      },
    });
  }

  /**
   * Fetches staff users from the server and updates the staffList property.
   * The staffList is populated with the response data if the status is true; otherwise, it is set to an empty array.
   */
  getStaffUsers() {
    let url = `/users/office-staff-list?uid=${this.api.getUserId()}&officeid=${this.api.getOfficeId()}&show=staff`;
    this.redux.getSetProjectInfo(false, 'office_staff', url).subscribe({
      next: response => {
        const { status, data } = response;
        if (status) {
          this.staffList = JSON.parse(JSON.stringify(data));
        } else {
          this.staffList = [];
        }
      },
    });
  }

  getTeamList() {
    this.sharedService.getDropDownItems('teams').subscribe({
      next: response => {
        this.teamList =
          response?.data?.map(team => {
            return { ...team, full_name: team?.name };
          }) || [];
        console.log('team list', this.teamList);
      },
    });
  }

  /**
   * Loads the ids of the teams the current user belongs to. When a lead's Owner
   * (or agent_responsible) is a team, membership here grants edit access — so a
   * user who owns the lead through their team can edit it. Mirrors the
   * `/teams?include=users` + user_members pattern used across the app.
   */
  getUserTeamIds() {
    const officeId = this.api.getOfficeId();
    if (!officeId) {
      return;
    }
    const currentUserId = this.api.getUserId();
    this.api.get('/teams?include=users&office_id=' + officeId).subscribe({
      next: response => {
        this.userTeamIds =
          response?.data
            ?.filter(team => Array.isArray(team?.user_members) && team.user_members.some(member => member?._id === currentUserId))
            ?.map(team => team?._id) || [];
      },
    });
  }

  /**
   * Updates the form with the listing values for beds, baths, value, listing type, car park number, pool, internal size, and external area.
   */
  getListingValue(element) {
    this.editLeadForm.patchValue({
      beds: element?.beds,
      baths: element?.baths,
      value: element?.value,
      listing_type: element?.listing_type,
      car: element?.car_park_no,
      pool: element?.pool,
      internal_size: element?.building_area,
      external_area: element?.land_area,
    });
  }

  /**
   * Handles the lead type change event. Updates the typeName and resets lead status and log.
   * Also triggers the allocation of lead status and lead source tags.
   */

  eoi_list: any[] = [];

  getEOIList() {
    let value = this.editLeadForm.controls['project'].value;
    if (value) {
      let project_id = value ? [value] : [];
      this._projectService.EoiShortList({ project_id: project_id }).subscribe({
        next: response => {
          this.eoi_list = [];
          let eoi_list = response?.data?.filter(ele => ele?.status_reason !== 'Cancelled');
          this.eoi_list = eoi_list?.map(ele => {
            const lot = `Lot: ${ele.lot_name}`; // Adjust width
            const status = ele.status_reason ? `Status: ${ele.status_reason}` : '';
            const purchaser = ele.client_name ? `Purchaser: ${ele.client_name}` : '';
            const displayParts = [lot];
            if (status) displayParts.push(status);
            if (purchaser) displayParts.push(purchaser);
            return {
              ...ele,
              display_name: displayParts.join(' | '),
            };
          });

          console.log('eeoi >>:', this.eoi_list);
        },
      });
    } else {
      this.eoi_list = [];
      this.editLeadForm.patchValue({
        lead_eoi: null,
      });
    }
  }

  createEOI() {
    let record = {
      project_id: this.editLeadForm.controls['project'].value,
    };
    this.lmService.addTabInLevels('EOI', 'createnew', record);
  }

  // onChangeLeadType(event) {
  //   this.typeName = event?.tag_slug;
  //   this.editLeadForm.patchValue({
  //     lead_status: null,
  //     lead_status_log: [],
  //   });
  //   this.allocateTagsToFields('lead-status');
  //   this.allocateTagsToFields('lead-source');
  // }

  /**
   * Manages the lead status based on the form values.
   * - If onLoadPatchForm is true, it updates the leadStatus with the corresponding log values.
   * - Otherwise, it compares the current lead status with the original value and updates the logs if any changes are found.
   */
  manageLeadStatus(onLoadPatchForm: boolean = false) {
    if (onLoadPatchForm) {
      let logs: any[] = this.editLeadForm.controls?.[`lead_status_log`].value || [];
      if (logs?.length === 0) {
        this.leadStatus = this.leadStatus?.map(result => {
          if (result?.hasOwnProperty('changed_on')) {
            delete result?.[`changed_on`];
          }
          return result;
        });
      } else if (this.leadStatus?.length > 0) {
        this.leadStatus = this.leadStatus?.map(result => {
          let foundedIndex: number = logs?.findIndex(local_tag => local_tag?.tag_name === result?.tag_name);
          if (foundedIndex > -1) {
            result = { ...result, changed_on: logs?.[foundedIndex]?.changed_on };
          }
          return result;
        });
      }
    } else {
      let local_lead_type = this.editLeadForm.controls?.[`lead_type`].value;
      if (!local_lead_type) {
        this.editLeadForm.patchValue({
          lead_status_log: null,
        });
      } else {
        let original_value = this.editLeadForm.controls?.[`lead_status`].value;
        let cloned_value = this.lead_detail?.[`lead_status`];
        if (original_value !== cloned_value) {
          this.leadStatus = this.leadStatus?.map(result => {
            if (result?.tag_name === original_value) {
              result = { ...result, changed_on: new Date() };
              let logs: any[] = this.editLeadForm.controls?.[`lead_status_log`].value || [];
              let foundedIndex = logs.findIndex(local_log => local_log?.tag_name === original_value);
              if (foundedIndex > -1) {
                logs[foundedIndex] = result;
              } else {
                logs?.push(result);
                this.editLeadForm.patchValue({
                  lead_status_log: logs,
                });
              }
            }
            return result;
          });
        }
      }
    }
  }
  /**
   * Handles the drag-and-drop event for tags. If the item is moved within the same container, it rearranges the order;
   * otherwise, it adds the tag to the form.
   */
  dropTag(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      // Reorder happens outside the form; addTagInForm() saves its own change.
      this.saveTagChange();
    } else {
      this.addTagInForm(event.currentIndex, event.item.data);
    }
  }

  /**
   * Adds a tag to the form at the specified index if the tag is not already attached.
   * If the tag is already attached, an error message is displayed.
   */
  addTagInForm(index: number, data) {
    let findInd = this.todo.find(el => el?._id == data?._id);
    if (!findInd) {
      this.todo?.splice(index, 0, data);
      this.saveTagChange();
    } else {
      this._toasterService.Error('Tag is already attached');
    }
  }

  getTasks(record = null) {
    let id = record?._id == null ? this.lead_detail?._id : record?._id;
    this.taskService.getLeadsTasks(id).subscribe({
      next: response => {
        this.load.hide();
        const { status, data } = response;
        if (status) {
          this.taskList = data
            ?.filter(el => el.taskStatusName !== 'Completed')
            .sort((a, b) => {
              let a1: any = new Date(a.created_at);
              let b1: any = new Date(b.created_at);
              return b1 - a1;
            });
          this.followUpTask = this.taskList.find(f => f?.follow_up == true);
          if (this.followUpTask) {
            this.follow_priority = this.followUpOptionsService.matchOptionId(this.followUpTask.due_date) ?? this.follow_priority;
          }
        } else {
          this.taskList = [];
        }
      },
      error: () => {
        this.taskList = [];
        this.load.hide();
      },
    });
  }
  gSuiteLogin() {
    var win = window.open(this.googleAuthUrl, 'windowname1', 'width=800, height=600');

    window.addEventListener(
      'message',
      event => {
        if (event.data === 'authenticated') {
          this.emailConnected = true;
          win.close();
          this.fetchEmails(this.lead_detail);
        }
      },
      false
    );
  }

  /**
   * Fetches the emails related to the given lead record and updates various properties.
   * - If connected, sets `emailConnected` to true, otherwise, updates the Google authentication URL.
   * - Also fetches the tasks related to the record and updates the attendees for the event.
   */
  fetchEmails(record) {
    this.leads.fetchEmailList(record?._id).subscribe({
      next: response => {
        const { connected, leadCalendarmanage, data } = response;
        this.leadCalendarManagePermission = leadCalendarmanage;
        if (connected) {
          this.emailConnected = true;
        } else {
          this.googleAuthUrl = data + '&state=' + this.api.getUserId();
        }
      },
    });

    this.getTasks(record);

    this.emailKeyword = '';
    this.isFetchedEmails = false;
    this.selectedLeadEmails = { inbox: [], sent: [] };
    let address = record?.listing_address ? record?.listing_address : record?.project_address;
    this.leads.fetchEmailList(record?._id, address, record?.email).subscribe({
      next: response => {
        const { status, data } = response;
        if (status) {
          const withDates = (rows: any[]) =>
            (rows ?? []).map(ele => ({
              ...ele,
              internalDate: this.formatInternalDate(ele?.internalDate),
              thread_Date: this.threadFormatDate(ele?.internalDate),
            }));
          this.isFetchedEmails = true;
          this.selectedLeadEmails.inbox = withDates(data?.inbox);
          this.selectedLeadEmails.sent = withDates(data?.sent);
          this.emailCopy = data;
        }
      },
      error: () => {
        // Without this the spinner never clears and the tab looks permanently empty.
        this.isFetchedEmails = true;
        this._toasterService.Error('We could not load emails for this lead. Please try again.');
      },
    });
  }

  /**
   * Formats the date into a more human-readable string, including the relative time.
   * - Converts the date to a localized string format including weekday, month, day, hour, and minute.
   * - Appends relative time to the formatted date.
   *
   * @param date - The internal date to format.
   * @returns A formatted date string.
   */
  threadFormatDate(date: Date): string {
    const internalDate = new Date(Number(date));
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: undefined, // Remove year if not needed
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };
    return `${internalDate.toLocaleDateString('en-US', options)} (${this.relativeTime(internalDate)})`;
  }

  /**
   * Returns a relative time string based on the difference between the current date and the given date.
   * - If the date is today, returns 'Today'.
   * - If the date is yesterday, returns 'Yesterday'.
   * - Otherwise, returns the number of days ago.
   *
   * @param date - The date to compare against the current date.
   * @returns A relative time string.
   */
  relativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const daysAgo = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (daysAgo === 0) return 'Today';
    if (daysAgo === 1) return 'Yesterday';
    return `${daysAgo} days ago`;
  }

  /**
   * Formats the internal date string into a more user-friendly format.
   * - If the date is today, returns the time in 'HH:mm' format.
   * - Otherwise, returns the date in 'MMM dd' format.
   *
   * @param internalDateStr - The internal date string (timestamp) to format.
   * @returns A formatted date or time string.
   */
  formatInternalDate(internalDateStr: string): string {
    const internalDate = new Date(Number(internalDateStr));
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    const isToday = internalDate.toDateString() === today.toDateString();

    if (isToday) {
      return internalDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return internalDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  customFilter = (term: string, item: any) => {
    term = term.toLowerCase();
    return item?.full_name?.toLowerCase()?.includes(term) || item?.project?.length > 0
      ? item?.project[0]?.Project_Name?.toLowerCase()?.includes(term)
      : item?.listing[0]?.property_address?.toLowerCase()?.includes(term) ||
          item?.email?.toLowerCase()?.includes(term) ||
          item?.lead_type?.toLowerCase()?.includes(term) ||
          item?.lead_status?.toLowerCase()?.includes(term);
  };

  /**
   * Fetches all lead information based on the lead's email.
   * - The lead's creation date is formatted using the moment.js library.
   */
  getAllLead() {
    // Backend returns an empty list for an empty email, so skip the request.
    if (!this.lead_detail?.email) {
      this.allAssignedLead = [];
      return;
    }
    this.leads.getLeadShortInfo(this.lead_detail?.email).subscribe({
      next: response => {
        this.allAssignedLead = response?.data?.map(el => {
          return (el = { ...el, created_at: moment(el?.created_at).format('d/MM/yy') });
        });
      },
    });
  }

  /**
   * Assigns an email to a selected lead.
   * - Sends the lead ID, office ID, and message ID to the API to assign the email to the lead.
   * - After successful assignment, fetches the lead's emails again after a 3-second delay.
   *
   * @param mail_detail - The email details to assign.
   */
  assignEmailToSelectedLead() {
    this.leads
      .assignEmailToLead({
        leadid: this.assignedLeadEmail,
        office_id: this.api.getOfficeId(),
        msgid: this.message_id,
      })
      .subscribe({
        next: response => {
          this.toaster.Success(response.message);
          this.message_id = null;
          this.assignedLeadEmail = null;
          this.showMailBar = false;
          setTimeout(() => {
            this.fetchEmails(this.lead_detail);
          }, 3000);
        },
      });
  }

  /**
   * Unassign an email from a selected lead.
   * - Sends the lead ID, office ID, and message ID to the API to unassign the email from the lead.
   * - After successful unassigned, fetches the lead's emails again after a 3-second delay.
   *
   * @param mail_detail - The email details to unassign.
   */
  unAssignEmailToSelectedLead(mail_detail) {
    this.remmiConfirm
      .ask({
        title: 'Unassign Email?',
        message: 'This email will be unlinked from the lead. It stays in the mailbox.',
        subject: mail_detail?.subject,
        confirmLabel: 'Unassign',
        tone: 'warning',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.leads
            .unassignEmailToLead({
              leadid: this.lead_detail?._id,
              office_id: this.api.getOfficeId(),
              msgid: mail_detail?.id,
            })
            .subscribe({
              next: response => {
                this.showMailBar = false;
                this.toaster.Success(response.message);
                setTimeout(() => {
                  this.fetchEmails(this.lead_detail);
                }, 3000);
              },
            });
        }
      });
  }

  /**
   * Marks an email as private.
   * - Sends the lead ID, office ID, message ID, and the 'private' status to the API to mark the email.
   * - After successful operation, fetches the lead's emails again after a 3-second delay.
   *
   * @param mail_detail - The email details to mark as private.
   * @param mark - The status to mark the email as (default is 'private').
   */
  markPrivate(mail_detail, mark = 'private') {
    this.leads
      .markEmailPrivate({
        office_id: this.api.getOfficeId(),
        leadid: this.lead_detail?._id,
        msgid: mail_detail.id,
        mark: mark,
      })
      .subscribe({
        next: response => {
          this.toaster.Success(response.message);
          this.showMailBar = false;
          setTimeout(() => {
            this.fetchEmails(this.lead_detail);
          }, 3000);
        },
      });
  }

  /**
   * Searches emails by a specified keyword and filters the emails in the 'sent' and 'inbox' arrays.
   * - Filters the emails in both 'sent' and 'inbox' by checking if the keyword is found in the snippet, to/from, or subject of the email.
   * - If no keyword is provided, it resets the 'sent' and 'inbox' arrays to their original values from `emailCopy`.
   *
   * @returns void
   */
  searchEmailByKeyword() {
    if (this.emailKeyword) {
      this.selectedLeadEmails['sent'] = this.emailCopy.sent;
      this.selectedLeadEmails['inbox'] = this.emailCopy.inbox;
      if (this.isSent) {
        let sent = this.selectedLeadEmails?.sent.filter(em => {
          if (
            em?.snippet.toLowerCase().includes(this.emailKeyword.toLowerCase()) ||
            em?.to.toLowerCase().includes(this.emailKeyword.toLowerCase()) ||
            em?.subject.toLowerCase().includes(this.emailKeyword.toLowerCase())
          ) {
            return em;
          }
        });
        this.selectedLeadEmails['sent'] = sent;
      }

      // inbox
      if (this.isInbox) {
        let inbox = this.selectedLeadEmails?.inbox.filter(em => {
          if (
            em?.snippet.toLowerCase().includes(this.emailKeyword.toLowerCase()) ||
            em?.from.toLowerCase().includes(this.emailKeyword.toLowerCase()) ||
            em?.subject.toLowerCase().includes(this.emailKeyword.toLowerCase())
          ) {
            return em;
          }
        });
        this.selectedLeadEmails['inbox'] = inbox;
      }
    } else {
      this.selectedLeadEmails['sent'] = this.emailCopy.sent;
      this.selectedLeadEmails['inbox'] = this.emailCopy.inbox;
    }
  }

  /**
   * Displays the details of a selected email.
   * - Sets the `fromOrTop` flag based on the email type (from or to).
   * - Displays the email details in the mail bar.
   *
   * @param mail - The selected email object.
   * @param fromOrTo - A flag to indicate whether the email is from or to.
   * @returns void
   */
  viewMailDetail(mail, fromOrTo) {
    this.fromOrTop = fromOrTo;
    this.showMailBar = true;
    this.mail_detail = mail;
  }

  /**
   * Prepares the UI for composing a new email.
   * - Resets any reply flags and shows the mail bar for composing.
   * - Sets the `isNewMail` flag based on whether it's a new email or a reply.
   *
   * @param isNew - A flag to indicate whether the email is new or a reply.
   * @returns void
   */
  showMailInner(isNew) {
    this.isReplying = false;
    this.isNewMail = isNew;
    this.showMailBar = true;
  }

  /**
   * Prepares the UI for replying to an email.
   * - Sets the `isReplying` flag to true and initializes the email form with the recipient and subject.
   * - If the subject does not already contain 'Re:', it adds 'Re:' to the beginning of the subject.
   *
   * @param isNew - A flag to indicate whether it's a new email or a reply.
   * @returns void
   */
  showMailInnerReply(isNew) {
    this.isReplying = true;
    this.isNewMail = isNew;
    this.showMailBar = true;
    this.tokenInput = { value: this.lead_detail?.email };
    this.composeEmail.patchValue({
      to: [{ full_name: '', email: this.tokenInput.value, role: '' }],
      subject: !this.mail_detail?.subject?.includes('Re') ? 'Re: ' + this.mail_detail?.subject : this.mail_detail?.subject,
    });
  }

  /**
   * Hides the mail bar and resets the compose email form.
   * - Closes the mail compose window and resets the compose email form.
   *
   * @returns void
   */
  hideMailInner() {
    this.showMailBar = this.openComposeEmail = false;
    this.composeEmail.reset();
    this.fromOrTop = '';
  }
  /**
   * Validates whether the input string is a valid email address.
   * - Uses a regular expression to check email format.
   *
   * @param email - The email string to validate.
   * @returns boolean | RegExpMatchArray - Returns match result or null if invalid.
   */
  validateEmail(email) {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }

  /**
   * Sends an email using the data from `composeEmail` form.
   * - Validates the form and ensures recipients are correctly set.
   * - Builds recipient list from individuals and teams.
   * - Sends the email via API and resets the form on success.
   * - If replying, attaches the original thread ID.
   *
   * @returns void
   */
  sendEmail() {
    this.submitEmail = true;
    // A recipient typed but not yet tokenised (no Enter/blur) is still a valid recipient.
    // `to` is null after a form reset, so seed from an empty list rather than spreading it.
    if (this.tokenInput?.value && !this.composeEmail.controls['to'].value?.length) {
      if (this.validateEmail(this.tokenInput.value.trim())) {
        this.composeEmail.patchValue({
          to: [...(this.composeEmail.value.to ?? []), { full_name: null, email: this.tokenInput.value.trim(), role: null }],
        });
        this.tokenInput.value = '';
      }
    }
    this.formHelper.validateAllFormFields(this.composeEmail);

    if (this.composeEmail.valid) {
      let threadid = '';

      this.submitEmail = true;
      let tos = '';
      this.composeEmail.value.to.forEach(itm => {
        if (itm.hasOwnProperty('role') == true && itm.role == 'team') {
          itm.user_members.forEach(itm_team => {
            tos += itm_team.email + ',';
          });
        } else {
          tos += itm.email + ',';
        }
      });

      tos = tos.slice(0, -1);
      let fields = {
        object_reference: null,
        user_id: this.api.getUserId(),
        femail: tos,
        subject: this.composeEmail.value.subject,
        message: this.composeEmail.value.message,
      };

      if (this.isReplying) {
        threadid = this.mail_detail.threadId;
      }
      if (threadid) {
        fields['threadid'] = threadid;
      }

      this.leads.sendEmail(fields).subscribe({
        next: () => {
          this._toasterService.Success('Email sent successfully');
          this.composeEmail.reset();
          this.composeEmail.patchValue({ to: [] });
          this.showMailBar = false;
          this.submitEmail = false;
          setTimeout(() => {
            this.fetchEmails(this.lead_detail);
          }, 3000);
        },
        error: err => {
          // Keep the draft on screen so the user can retry without retyping.
          this.submitEmail = false;
          this._toasterService.Error(err?.error?.message || 'Could not send the email. Please try again.');
        },
      });
    }
  }

  onChangeLeadTypeRequirement() {
    // Loads related data based on the selected lead category
    if (this.editLeadForm.controls?.[`lead_category`].value === 'project') {
      this.getProjects();
      this.editLeadForm.patchValue({
        listing: null,
        property: null,
      });
    } else if (this.editLeadForm.controls?.[`lead_category`].value === 'listing') {
      this.getAllListings();
      this.eoi_list = [];
      this.editLeadForm.patchValue({
        listing: null,
        property: null,
      });
    } else if (this.editLeadForm.controls?.[`lead_category`].value === 'property') {
      this.getProperty();
      this.eoi_list = [];
      this.editLeadForm.patchValue({
        project: null,
        lead_eoi: null,
      });
    }

    // Resets dependent fields in the form when lead category changes
    this.editLeadForm.patchValue({
      project: null,
      apartment: null,
      intended_use: null,
      time_frame: null,
      value: null,
      beds: null,
      baths: null,
      car: null,
      pool: null,
      spa: null,
      land_area: null,
      internal_size: null,
      external_area: null,
      listing: null,
      listing_type: null,
    });
  }

  getProjects() {
    // Fetches project list to populate the dropdown when user selects 'project' category
    this.sharedService.getDropDownItems('projects').subscribe({
      next: response => {
        this.projectList = [];
        this.projectList = response?.data;
      },
    });
  }

  getProperty() {
    // Fetches all property data to populate dropdown or list
    this.sharedService.getDropDownItems('property').subscribe({
      next: response => {
        this.all_property = [];
        this.all_property = response?.data;
      },
    });
  }

  getAllListings() {
    // Fetches all listings based on office ID, used when 'listing' category is selected
    if (this.api.getOfficeId()) {
      this.listingService.getShortListing().subscribe({
        next: response => {
          const { status, data } = response;
          if (status) {
            this.all_listing = [];
            this.all_listing = data;
          } else {
            this.all_listing = [];
          }
        },
        error: () => {
          this.all_listing = [];
        },
      });
    }
  }

  //--------------------------------------------- Tag Manager ---------------------------------------------

  closeTagManger() {
    // Closes the tag manager popup and resets tag search input
    this.lmService.tag_manager = false;
    this.resetTagSearch();
  }

  resetTagSearch() {
    // Clears the tag search keyword and resets tag list to original data
    this.keyword_tags = null;
    this.tagsList = this.tagsDataCopy;
  }

  pushTagToArray(e) {
    // Handles logic when a tag is entered, checking for duplicates
    let tagArr = this.tagsList.find(tagArr => tagArr.module_type == this.module_type?.name);
    if (tagArr?.data) {
      // Checks if the tag already exists
      let tag_exist = tagArr?.data?.find(tag => tag?.tag_name?.toLowerCase().trim() == e.value.toLowerCase().trim());

      let tag_in_new_tag = [];

      // Checks if tag is already entered in the new_tag array
      if (this.new_tag?.length) {
        tag_in_new_tag = this.new_tag.filter(tag => tag?.toLowerCase().trim() == e.value.toLowerCase().trim());
      }

      // Shows error if duplicate tag exists and removes it from new_tag array
      if (tag_exist) {
        this.new_tag.splice(this.new_tag.indexOf(e.value.trim()), 1);
        this._toasterService.Error('Tag already exist');
      } else if (tag_in_new_tag?.length > 1) {
        this.getUnique();
        this._toasterService.Error('Tag is already entered');
      }
    }
  }

  getUnique() {
    // Removes duplicate tags from new_tag array and keeps only unique entries
    var uniqueArray = [];
    for (var value of this.new_tag) {
      if (uniqueArray.indexOf(value.toLowerCase().trim()) === -1) {
        uniqueArray.push(value.trim());
      }
    }
    this.new_tag = uniqueArray;
  }

  createTag() {
    // Creates a new tag or multiple tags based on user input
    this.loading = true;
    let payLoad = {};

    // Handles bulk or single tag creation based on input length
    if (this.new_tag?.length > 1) {
      payLoad = {
        'tag_name[]': this.new_tag,
        module_id: this.lead_tag_module_id,
        type_id: this.tag_type,
        office_id: this.api.getOfficeId(),
        user_id: this.api.getUserId(),
      };
    } else {
      payLoad = {
        'tag_name[]': this.new_tag,
        module_id: this.lead_tag_module_id,
        type_id: this.tag_type,
        office_id: this.api.getOfficeId(),
        user_id: this.api.getUserId(),
      };
    }

    // Removes type_id if not selected
    if (!this.tag_type) {
      delete payLoad['type_id'];
    }

    // Calls API to create tag(s) and handles success and error responses
    this.tagMangerService.addTag(payLoad).subscribe({
      next: data => {
        if (data?.status == true) {
          this._toasterService.Success('Tag successfully created');
          this.getLeadTags();
          this.new_tag = [];
          this.tag_type = null;
          this.new_tag_popup = false;
        } else {
          this._toasterService.Error(data?.message);
        }
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this._toasterService.Error(error?.error?.message ? error?.error?.message : error?.message);
      },
    });
  }

  /**
   * Creates a new tag type using the entered tag_type value and updates the list.
   */
  createTagType() {
    this.loading = true;

    // Prepare payload for new tag type
    let payLoad = {
      module_id: this.tagsList?.[0]?.module_id,
      type: 'tag_types',
      name: this.tag_type,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    };

    // API call to add new tag type
    this.tagMangerService.addTag(payLoad).subscribe({
      next: data => {
        if (data?.status == true) {
          this._toasterService.Success('Tag type successfully created');
          this.getLeadTags(); // refresh tags
          this.tag_type = null;
          this.new_tagType_popup = false;
          this.module_type = null;
        } else {
          this._toasterService.Error(data?.message);
        }
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this._toasterService.Error(error?.error?.message ? error?.error?.message : error?.message);
      },
    });
  }

  /**
   * Opens the "Create New Tag Type" popup if the selected value matched.
   */
  isNewTypeModel(event) {
    if (event?.value == 'createnew') {
      this.new_tagType_popup = true;
      this.module_type = null;
      this.tag_type = null;
    }
  }

  /**
   * Removes a public tag by index from the todo list.
   */
  removePublicTag(i) {
    this.remmiConfirm
      .ask({
        title: 'Remove Tag?',
        message: 'This tag will be removed from the lead and saved straight away.',
        subject: this.todo?.[i]?.tag_name,
        confirmLabel: 'Remove',
        tone: 'warning',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.todo?.splice(i, 1);
          this.saveTagChange();
        }
      });
  }

  /**
   * Tags aren't form controls, so their changes are saved by hand. The Tag Manager also works
   * outside edit mode, where the edit-session auto-save never runs — there the change is saved
   * straight away instead (same silent save the contact-attempt note uses).
   */
  private saveTagChange(): void {
    if (this.isLeadEdit) {
      this.scheduleAutoSave();
      return;
    }
    if (!this.isLeadDetailLoaded || !this.lead_detail?._id || !this.isAllowEdit()) {
      return;
    }
    this.requestSilentSave();
  }

  // Function triggered when CKEditor is ready – moves toolbar and removes branding
  onReady(editor) {
    editor.editing.view.document.on('focus', () => {
      let poweredByCKeditorElement = document.querySelector('.ck-body-wrapper');
      if (poweredByCKeditorElement) {
        poweredByCKeditorElement.remove();
      }
    });
    editor.ui.getEditableElement().parentElement.insertBefore(editor.ui.view.toolbar.element, editor.ui.getEditableElement());
  }

  // Function to place CKEditor toolbar above the editor
  emailEditor(editor) {
    editor.ui.getEditableElement().parentElement.insertBefore(editor.ui.view.toolbar.element, editor.ui.getEditableElement());
  }

  // Function to handle reordering of lead actions via drag-and-drop
  // Function to check if a lead step is activated based on current lead status
  isLeadActivated(step: any) {
    let currentValue = this.editLeadForm.controls?.['lead_status']?.value;
    return (
      this.editLeadForm.controls?.['lead_status_log']?.value?.filter(value => value?.tag_name === step?.tag_name && currentValue === step?.tag_name)
        ?.length > 0
    );
  }

  // Function to fetch email suggestions based on search query
  search(event) {
    let params = {
      page: 1,
      per_page: 9999999,
      search: event.query,
      uid: this.api.getUserId(),
      officeid: this.api.getOfficeId(),
    };
    this.leads.getSuggestionEmail(params).subscribe({
      next: response => {
        const { data, teams } = response;
        this.emailToSuggestion = [];
        this.emailToSuggestion = [...data, ...teams];
      },
      error: () => {
        this.emailToSuggestion = [];
      },
    });
  }

  /** Avatar initials for a recipient suggestion, falling back to the email so the
   * chip never renders as "undefinedundefined" for contacts with no name. */
  suggestionInitials(item: any): string {
    const first = item?.first_name?.trim() ?? '';
    const last = item?.last_name?.trim() ?? '';
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    const name = item?.full_name?.trim() || item?.email?.trim() || '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  // Handles key up event to add email to the 'to' field in composeEmail form when 'Enter' or 'Space' is pressed
  onKeyUpGetEmailToStatus(event: KeyboardEvent) {
    let tokenInput = event.srcElement as any;
    this.tokenInput = event.srcElement as any;
    if (event.key == 'Enter' || event.key == ' ') {
      if (tokenInput.value) {
        if (this.validateEmail(tokenInput.value.trim())) {
          const currentTo = this.composeEmail.value.to || [];
          this.composeEmail.patchValue({ to: [...currentTo, { full_name: null, email: tokenInput.value, role: null }] });
          tokenInput.value = '';
        }
      }
    }
  }

  jobTypes: any = null;
  taskStatuses: any = null;
  priorities: any = null;

  getTaskStatusTypes() {
    this.taskService.getTypes({ office_id: this.api.getOfficeId() }).subscribe({
      next: response => {
        this.jobTypes = response?.data?.find(ele => ele?.name == 'Follow Up');
      },
    });

    this.taskService.getStatuses({ office_id: this.api.getOfficeId() }).subscribe({
      next: response => {
        this.taskStatuses = response?.data?.find(ele => ele?.name == 'To Do' || ele?.name == 'In Progress');
      },
    });

    this.taskService.getPriorities({ office_id: this.api.getOfficeId() }).subscribe({
      next: response => {
        this.priorities = response?.data?.find(ele => ele?.name == 'Medium');
      },
    });
  }

  /**
   * The follow-up task the lead's Follow Up menu creates — shared with the No Response box's own booking.
   * No id goes out as "" or "undefined": the task API casts ids to ObjectIds and rejected the whole
   * request ("…12 bytes or a string of 24 hex characters") for a lead with no project or agent.
   */
  private followUpTaskPayload(dueDate: any, taskTime: string): any {
    const assignee = this.lead_detail?.agent_responsible || this.api.getUserId();
    return {
      is_later: 0,
      title: `Follow Up: ${this.lead_detail.full_name}`, // title
      assignedUsers: assignee ? [`${assignee}`] : [], // Staff (in lead agent_responsible)
      assignedTeams: [],
      job_type_id: this.jobTypes?._id, // Task Type _id
      start_date: dueDate,
      due_date: dueDate,
      task_status_id: this.taskStatuses?._id, // Task Status _id
      property_id: null,
      properties_id: null,
      task_category_id: null,
      priority_id: this.priorities?._id, // Priority _id
      description: '',
      isRecurring: null,
      recurringType: null,
      recurringEnds: null,
      logos: null,
      office_id: this.lead_detail?.office_id || this.api.getOfficeId(), // Office id
      task_check_list: '[]',
      lead_id: this.lead_detail._id, // lead _id
      SaveToGoogleCalendar: null,
      // contact_id: this.lead_detail?.contactid ? [`${this.lead_detail?.contactid}`] : [], // Contact (in lead contactid)
      project_id: this.lead_detail?.project || null, // Property (in lead project)
      apartment_id: null,
      follow_up: true,
      property_project_type: 'project', // same for follow-up
      task_time: taskTime, // Task Time
      reminder: null,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  // Creates a follow-up task based on lead details
  followUp = (isCustom: boolean = false) => {
    if (!this.lead_detail) {
      return;
    }
    const data = this.followUpTaskPayload(this.quickFollowUpSelection?.dueDate, this.quickFollowUpSelection?.taskTime);

    // Custom follow-up logic
    if (isCustom) {
      if (!this.follow_up_date) {
        this.toaster.Error('Please select the date');
        return;
      }
      if (!this.follow_up_time) {
        this.toaster.Error('Please select the time');
        return;
      }

      data.due_date = this.follow_up_date;
      data.start_date = this.follow_up_date;
      data.task_time = this.follow_up_time;
    }

    this.is_follow = isCustom;

    this.taskService.addTask(data).subscribe({
      next: _ => {
        if (!isCustom) {
          this.follow_priority = '';
          this.follow_up_date = null;
          // Back to the 8am default so "Pick date & time" opens pre-filled, as it does before any follow up.
          this.follow_up_time = '08:00am';
        }
        this.toaster.Success('Follow up added successfully');
        this.getTasks();
      },
    });
  };

  quickFollowUpSelection: FollowUpSelection = null;
  closeFollowUpPanel(): void {
    this.is_follow = false;
  }

  toggleFollowUpDatePicker(): void {
    this.is_pick_date = !this.is_pick_date;
  }

  setFollowUpTime(time: string): void {
    this.follow_up_time = time;
  }

  onQuickFollowUpSelect(selection: FollowUpSelection): void {
    this.quickFollowUpSelection = selection;
    this.follow_priority = selection.optionId;
    this.followUp();
  }

  selectDates = eve => {
    const d = new Date(eve);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    this.follow_up_date = new Date(`${year}-${month}-${day}`);
    this.followUp(true);
  };

  // Gets the next weekday time based on the current day
  // getNextWeekdayTime(day) {
  //   const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  //   const today = new Date().getDay();
  //   let nextDayIndex = (today + day) % 7;
  //   if (nextDayIndex === 0) {
  //     nextDayIndex = 1;
  //   } else if (nextDayIndex === 6) {
  //     nextDayIndex = 1;
  //   }
  //   const nextDayName = days[nextDayIndex];
  //   return `${nextDayName}, 08:00am`;
  // }

  // Formats the input date into a more readable format
  formatDate(inputDate) {
    const date = new Date(inputDate);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayOfWeek = days[date.getDay()];
    const dayOfMonth = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const am_pm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

    return `${dayOfWeek}, ${dayOfMonth} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${am_pm}`;
  }

  createdDate(inputDate, time: string = '') {
    const date = inputDate;
    const formattedDate = time ? moment(date).format('ddd, DD MMM YYYY') : moment(date).format('ddd, DD MMM YYYY, h:mma');
    return time ? `${formattedDate}, ${time}` : `${formattedDate}`;
  }

  // Tabs configuration for different sections
  tabs = [
    {
      id: 'leadover',
      label: 'Lead Overview',
      icon: 'assets/img/menuIcon/lead_icon.svg',
      flag: 'lead_overview',
      action: null,
    },
    {
      id: 'appointment',
      flag: 'project_appointment',
      label: 'Appointment',
      icon: 'assets/img/calendar-time.svg',
      action: () => {},
    },
    {
      id: 'email',
      flag: 'email',
      label: 'Email',
      icon: 'assets/img/menuIcon/lead_icon.svg',
      action: () => {
        this.fetchEmails(this.lead_detail);
      },
    },
    {
      id: 'sms',
      flag: 'sms',
      label: 'SMS',
      icon: 'assets/img/dashboadIcon/sms.svg',
      action: () => {
        this.loadSmsPanel();
      },
    },
    {
      id: 'history',
      flag: 'history',
      label: 'History',
      icon: 'assets/img/dashboadIcon/history.svg',
      action: () => {
        this.message.getModuleHistory(true);
      },
    },
    {
      id: 'horizon',
      flag: 'horizon',
      label: 'Horizon',
      icon: 'assets/img/menuIcon/lead_icon.svg',
      action: null,
    },
  ];

  // Handles tab click and executes the corresponding action
  onTabClick(index: number, action?: () => void): void {
    this.tabIndexValue = index;
    this.tabIndexValue == 0 && this.getProjectAppointments(this.lead_detail);
    if (action) {
      action();
    }
  }

  getLeadProjectId(): string | null {
    const p = this.lead_detail?.project;
    if (!p) {
      return null;
    }
    if (typeof p === 'string') {
      return p;
    }
    if (Array.isArray(p) && p.length) {
      const first = p[0];
      return typeof first === 'string' ? first : (first?._id ?? null);
    }
    if (typeof p === 'object' && p?._id) {
      return p._id;
    }
    return null;
  }

  private smsToastError(err: any): void {
    const msg = typeof err === 'string' ? err : err?.message;
    if (msg) {
      this.toaster.Error(msg);
    }
  }

  /** Plain SMS body with http(s) URLs turned into links that open in a new tab; rest is HTML-escaped. */
  smsMessageBodyHtml(body: string | null | undefined): SafeHtml {
    const raw = body == null ? '' : String(body);
    if (!raw) {
      return this.domSanitizer.bypassSecurityTrustHtml('');
    }
    const urlRegex = /https?:\/\/[^\s<]+/gi;
    const segments: string[] = [];
    let lastIndex = 0;
    for (const match of raw.matchAll(urlRegex)) {
      const fullMatch = match[0];
      const start = match.index ?? 0;
      segments.push(this.escapeHtmlForSmsBody(raw.slice(lastIndex, start)));
      try {
        const parsed = new URL(fullMatch);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          const hrefAttr = this.escapeHtmlAttributeForHref(parsed.href);
          segments.push(`<a class="lead-sms-bubble-link" href="${hrefAttr}" target="_blank" rel="noopener noreferrer">Calendar Booking</a>`);
        } else {
          segments.push(this.escapeHtmlForSmsBody(fullMatch));
        }
      } catch {
        segments.push(this.escapeHtmlForSmsBody(fullMatch));
      }
      lastIndex = start + fullMatch.length;
    }
    segments.push(this.escapeHtmlForSmsBody(raw.slice(lastIndex)));
    return this.domSanitizer.bypassSecurityTrustHtml(segments.join(''));
  }

  private escapeHtmlForSmsBody(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private escapeHtmlAttributeForHref(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/'/g, '&#39;');
  }

  loadSmsPanel(): void {
    const leadId = this.lead_detail?._id;
    if (!leadId) {
      return;
    }
    this.smsPanelLoading = true;
    this.smsConversation = null;
    this.smsMessages = [];
    forkJoin({
      conversation: this.leadSmsService.getConversationByLead(leadId).pipe(catchError(() => of({ status: false, data: null }))),
      messages: this.leadSmsService.getMessagesByLead(leadId).pipe(
        catchError(err => {
          this.smsToastError(err);
          return of({ status: false, data: [] });
        })
      ),
    })
      .pipe(finalize(() => (this.smsPanelLoading = false)))
      .subscribe({
        next: ({ conversation, messages }) => {
          if (conversation?.status && conversation?.data) {
            this.smsConversation = conversation.data;
          }
          if (messages?.status && Array.isArray(messages?.data)) {
            this.smsMessages = [...messages.data].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          }
        },
      });
  }

  refreshSmsThread(): void {
    this.loadSmsPanel();
  }

  sendLeadSms(): void {
    const leadId = this.lead_detail?._id;
    const text = (this.smsBody || '').trim();
    if (!leadId || !text) {
      this.toaster.Error('Please enter a message to send');
      return;
    }
    this.smsSendLoading = true;
    this.leadSmsService
      .sendSms(leadId, text)
      .pipe(finalize(() => (this.smsSendLoading = false)))
      .subscribe({
        next: res => {
          if (res?.status) {
            this.toaster.Success(res?.message || 'SMS sent');
            this.smsBody = '';
            this.loadSmsPanel();
          } else {
            this.toaster.Error(res?.message || 'Could not send SMS');
          }
        },
        error: err => this.smsToastError(err),
      });
  }

  openInspection() {
    this.tabIndexValue = 1;
  }

  getContactName(): string {
    if (this.lead_detail?.contact_detail?.length > 0) {
      return this.lead_detail.contact_detail[0].full_name;
    }
    return `${this.lead_detail?.first_name || ''} ${this.lead_detail?.last_name || ''}`.trim();
  }

  // ---- External EOI link ---------------------------------------------------
  // Sends the buyer-facing EOI form straight from a lead, and shows the links
  // already issued for it. See ExternalEoiGenerateModalComponent and
  // ExternalEoiLinksComponent.

  showExternalEoiModal = false;

  /** A link is scoped to a project, so the action needs the lead to have one. */
  get canGenerateExternalEoi(): boolean {
    return !!this.getLeadProjectId();
  }

  get externalEoiProjectId(): string | null {
    return this.getLeadProjectId();
  }

  get externalEoiProjectName(): string {
    // `lead_detail.project`, when populated, already carries the name — reading it directly
    // avoids depending on `lmService.ProjectList` (a separately-loaded lookup for the project
    // picker elsewhere on this page, which is not guaranteed to include every project, or to have
    // loaded yet by the time this modal opens) and matches the name already on screen in the
    // breadcrumb. Falls back to the cached name from `lead_detail`'s setter (see
    // `cachedLeadProjectName`) for when a later reload no longer carries it.
    const fromLead = EditLeadDetailComponent.extractProjectName(this.lead_detail) || this.cachedLeadProjectName;
    if (fromLead) return fromLead;

    const id = this.getLeadProjectId();
    if (!id) {
      return '';
    }
    return this.lmService?.ProjectList?.find((p: any) => p?._id === id)?.Project_Name || '';
  }

  /**
   * `contactid` is deleted off `lead_detail` once assignments load, so read the
   * hydrated `contact_detail` first.
   */
  get externalEoiContactId(): string | null {
    return this.lead_detail?.contact_detail?.[0]?._id || this.lead_detail?.contactid || null;
  }

  get externalEoiContactEmail(): string {
    return this.lead_detail?.contact_detail?.[0]?.email || this.lead_detail?.email || '';
  }

  /**
   * The lead's mobile, so a link generated from a lead that has no contact
   * record still carries the buyer's details through — the agent should never
   * have to go and find the person they are already looking at.
   */
  get externalEoiContactMobile(): string {
    return this.lead_detail?.contact_detail?.[0]?.mobile_no || this.lead_detail?.mobile_no || this.lead_detail?.mobile || '';
  }

  openExternalEoiModal(): void {
    this.showExternalEoiModal = true;
    this.ensureExternalEoiProjectName();
  }

  /**
   * `externalEoiProjectName` falls through to '' when the lead's `project` reference carries no
   * name and `lmService.ProjectList` hasn't loaded (or doesn't include this project) — reported
   * live as the "Send External EOI Form" dialog's Project field showing "—" even though the lead
   * plainly has one. Fetched directly and cached the same way the `lead_detail` setter above does,
   * so the dialog has a real name instead of falling back to a lookup that can also come up empty.
   */
  private ensureExternalEoiProjectName(): void {
    if (this.cachedLeadProjectName || EditLeadDetailComponent.extractProjectName(this.lead_detail)) return;
    const id = this.getLeadProjectId();
    if (!id) return;
    this._projectService.getProjectShortDetails(id).subscribe({
      next: (res: any) => {
        const name = res?.data?.Project_Name;
        if (name) this.cachedLeadProjectName = name;
      },
      error: () => {},
    });
  }

  closeExternalEoiModal(): void {
    this.showExternalEoiModal = false;
  }

  isNoteCollapsed = false;
  notesList = (eve: any[]) => {
    if (eve?.length > 0) {
      this.isNoteCollapsed = false;
    } else {
      this.isNoteCollapsed = true;
    }
  };
  isRequirementsEmpty(): boolean {
    if (!this.editLeadForm) return true;

    const leadType: string[] = this.editLeadForm.get('lead_type')?.value || [];
    let controlsToCheck: string[] = [];

    if (leadType.includes('Agent')) {
      controlsToCheck = [
        'project_information_sent',
        'project_information_sent_date',
        'agreement_sent_date',
        'agreement',
        'personal_briefing_date',
        'personal_briefing_time',
        'personal_briefing_complete',
        'onsite_training_date',
        'onsite_training_time',
        'onsite_training_complete',
        'comments',
      ];
    }

    if (leadType.includes('Buyer')) {
      controlsToCheck = [
        'listing_type',
        'price_range',
        'beds',
        'baths',
        'car',
        'reason_for_buying',
        'min_land_area',
        'max_land_area',
        'established_property',
        'intended_use',
        'time_frame',
        'age_group',
        'outdoor_feature',
        'indoor_feature',
      ];
    }

    if (leadType.includes('Seller')) {
      controlsToCheck = [
        'address',
        'listing_type',
        'price_range',
        'beds',
        'baths',
        'car',
        'reason_for_selling',
        'current_purpose',
        'time_frame',
        'age_group',
        'tenancy_details',
        'tenancy_end_date',
        'features',
      ];
    }

    if (leadType.includes('Developer')) {
      controlsToCheck = [
        'development_type',
        'project_address',
        'project_suburb',
        'project_status',
        'lot_quantity',
        'current_market',
        'product_mix',
        'price_range',
        'time_frame',
        'levels',
        'amenities',
      ];
    }

    let ele = controlsToCheck.every(controlName => {
      const value = this.editLeadForm.get(controlName)?.value;

      if (Array.isArray(value)) {
        return value.length === 0;
      }

      return value === null || value === undefined || value === '';
    });

    return ele || false;
  }

  /**
   * Drives the "(To Be Completed)" heading suffix and the collapsed state of a section.
   * In view mode it simply mirrors `isEmpty`. In edit mode the flag is sticky: once the
   * user has typed something into the section, clearing it again does not bring the label
   * back (or collapse the box) until the lead returns to view mode.
   */
  isSectionIncomplete(section: string, isEmpty: boolean): boolean {
    if (!this.isLeadEdit) return isEmpty;

    if (!isEmpty) {
      this.sectionsFilledInEdit.add(section);
      return false;
    }

    return !this.sectionsFilledInEdit.has(section);
  }

  isInspectionEmpty(): boolean {
    if (!this.editLeadForm) return true;

    const date = this.editLeadForm.get('inspection_dateTime')?.value;
    const booked = this.editLeadForm.get('inspection_booked')?.value;
    const complete = this.editLeadForm.get('inspection_complete')?.value;
    const cancelled = this.editLeadForm.get('inspection_cancelled')?.value;
    const time = this.inspectionTime;

    return (
      (date === null || date === undefined || date === '') &&
      (time === null || time === undefined || time === '') &&
      booked !== true &&
      complete !== true &&
      cancelled !== true
    );
  }

  closeFollowUp(data: any) {
    // Fetch the task statuses
    this.load.show();
    this.taskService.getStatuses({ office_id: this.api.getOfficeId() }).subscribe({
      next: response => {
        this.taskStatuses = response.data; // Store task statuses
        let id = this.taskStatuses.filter(f => f.name === 'Closed' || f.name === 'Completed')[0]._id;
        let assignedTeams = [];
        let assignedUsers = [];
        data?.assignedTeams?.data?.forEach(f => {
          assignedTeams.push(f._id);
        });
        data?.assignToUsers?.data?.forEach(f => {
          assignedUsers.push(f._id);
        });
        let tasks = {
          is_later: data?.is_later,
          title: data?.title,
          assignedTeams: assignedTeams,
          assignedUsers: assignedTeams,
          job_type_id: data?.job_type_id,
          start_date: data?.start_date,
          due_date: data?.due_date,
          task_status_id: id,
          property_id: data?.property_id,
          properties_id: data?.properties_id,
          priority_id: data?.priority_id,
          SaveToGoogleCalendar: data?.SaveToGoogleCalendar,
          description: data?.description,
          isRecurring: data?.isRecurring,
          recurringType: data?.recurringType,
          recurringEnds: data?.recurringEnds,
          logos: data?.logos,
          lead_id: data?.lead_id,
          task_check_list: data?.task_check_list,
          project_id: data?.project_id,
          contact_id: data?.contact_id,
          apartment_id: data?.apartment_id,
          property_project_type: data?.property_project_type,
          task_time: data?.task_time,
          reminder: data?.reminder,
          time_zone: data?.time_zone,
          is_sub_task: data?.is_sub_task,
        };

        this.taskService.updateTask(data?._id, tasks).subscribe({
          next: _response => {
            this.getTasks();
            this.follow_priority = '';
          },
          error: _err => {
            this.load.hide();
          },
        });
      },
      error: _err => {
        this.load.hide();
      },
    });
  }

  isComission: boolean = false;
  _activity: string = 'status';
  commission_list: any[] = [];

  onClickOutSide = (isClickOutSideNote: Event) => {
    if (isClickOutSideNote['bol'] === false && this.isComission) {
      // Dismissing by clicking outside used to throw the typed note away — persist it first.
      this.flushContactNoteDraft();
      this.isComission = false;
      this.commissionType = [];
    }
  };

  commissionType: any[] = [];
  commissionTypeAdded: any[] = [];
  commissionStats: any[] = [];

  /** Contact-attempt log count for Activity → Communication tab (shown beside label). */
  get numberOfRecords(): number {
    return this.commission_list?.length ?? 0;
  }

  addCommission = (status = 'Contact Attempted') => {
    let data = {
      lead_id: this.lead_detail?._id,
      channels: this.commissionTypeAdded,
      office_id: this.api.getOfficeId(),
    };
    this.load.show();
    this.updateReportNotes(); // ye notes update karta hai, summary auto-trigger hogi valueChanges se
    this.leads.addCommission(data).subscribe({
      next: _response => {
        this.load.hide();
        this.commissionTypeAdded = [];
        this.editLeadForm.patchValue({
          lead_status: status, // Only update lead status to "Contact Attempted" if it's currently "New"
        });
        this.lead_detail = { ...this.lead_detail, lead_status: status };
        this.loadCommission();
        this.syncLeadDetailReportNotesFromForm();
        this.requestSilentSave();
      },
      error: _err => {
        this.isComission = false;
        this.load.hide();
      },
    });
  };

  loadCommission = () => {
    this.api.get(`/leads/contact-attempted?lead_id=${this.lead_detail?._id}`).subscribe({
      next: response => {
        this.commission_list = response?.data;
      },
      error: _err => {},
    });
    this.api.get(`/leads/contact-attempted-stats?lead_id=${this.lead_detail?._id}`).subscribe({
      next: response => {
        this.commissionStats = response?.data?.rows;
        this.reportNotesSummaryComponent?.maybeBackfillReportNotesSummaryAfterLoad();
      },
      error: _err => {},
    });
  };

  onChangeCompleted = (eve: any) => {
    if (eve?.checked) {
      this.syncInspectionStatus('Completed');
    } else {
      this.syncInspectionStatus();
    }
    if (eve?.checked) {
      // Auto-set lead status to post-appointment nurturing
      this.editLeadForm.get('lead_status').setValue('Nurturing (Post Appointment)');

      // Trigger summary update
      const currentNotes = this.editLeadForm.controls['report_notes'].value || '';
      this.reportNotesSummaryComponent?.autoUpdateSummary(currentNotes);
      return;
    }

    // When completed is removed, fall back to pre-appointment if inspection is still active/cancelled.
    const isBooked = this.editLeadForm.get('inspection_booked')?.value === true;
    const isCancelled = this.editLeadForm.get('inspection_cancelled')?.value === true;
    if (isBooked || isCancelled) {
      this.editLeadForm.get('lead_status').setValue('Nurturing (Pre Appointment)');
    }
  };

  onChangeBooked = (eve: any) => {
    if (eve?.checked) {
      this.syncInspectionStatus('Booked');
    } else {
      this.syncInspectionStatus();
    }
    if (eve?.checked) {
      const isCompleted = this.editLeadForm.get('inspection_complete')?.value === true;
      if (!isCompleted) {
        this.editLeadForm.get('lead_status').setValue('Nurturing (Pre Appointment)');
      }
    }
  };

  onChangeCancelled = (eve: any) => {
    if (eve?.checked) {
      this.syncInspectionStatus('Cancelled');
    } else {
      this.syncInspectionStatus();
    }
    if (eve?.checked) {
      this.editLeadForm.get('inspection_dateTime')?.setValue(null);
      this.inspectionTime = null;
      const isCompleted = this.editLeadForm.get('inspection_complete')?.value === true;
      // Cancelled inspection should stay in pre-appointment unless explicitly completed.
      if (!isCompleted) {
        this.editLeadForm.get('lead_status').setValue('Nurturing (Pre Appointment)');
      }
    }
  };

  persistReportNotesSummary(): void {
    if (!this.isLeadDetailLoaded) {
      return;
    }
    this.syncLeadDetailReportNotesFromForm();
    this.requestSilentSave({ fromSummaryPersist: true });
  }

  /**
   * Every successful lead update — manual Save, Save & Close, or a background auto-save — asks
   * /ai-lead-summary for a fresh summary, so the stored text never lags the lead. The child
   * debounces (400ms) and switchMaps, so a burst of auto-saves still costs one AI call, and
   * autoUpdateSummary() stays out of the way once the user has written the summary by hand.
   * The persist save it triggers carries fromSummaryPersist, which is what stops the two looping.
   */
  private refreshAiLeadSummaryAfterSave(): void {
    const currentNotes = this.editLeadForm?.controls?.['report_notes']?.value || '';
    this.reportNotesSummaryComponent?.autoUpdateSummary(currentNotes, true);
  }

  private syncLeadDetailReportNotesFromForm(): void {
    this.lead_detail = {
      ...this.lead_detail,
      report_notes: this.editLeadForm?.controls?.['report_notes']?.value || '',
      report_notes_summary: this.editLeadForm?.controls?.['report_notes_summary']?.value || '',
    };
  }

  private syncInspectionStatus(latestTrueStatus?: 'Booked' | 'Completed' | 'Cancelled'): void {
    if (latestTrueStatus) {
      this.editLeadForm.patchValue({ latest_inspection_status: latestTrueStatus }, { emitEvent: false });
      return;
    }

    const isBooked = this.editLeadForm.get('inspection_booked')?.value === true;
    const isCompleted = this.editLeadForm.get('inspection_complete')?.value === true;
    const isCancelled = this.editLeadForm.get('inspection_cancelled')?.value === true;
    const currentStatus = this.editLeadForm.get('latest_inspection_status')?.value as 'Booked' | 'Completed' | 'Cancelled' | '';

    let status = '';
    // Preserve latest picked status if that checkbox is still true.
    if (currentStatus === 'Cancelled' && isCancelled) {
      status = 'Cancelled';
    } else if (currentStatus === 'Completed' && isCompleted) {
      status = 'Completed';
    } else if (currentStatus === 'Booked' && isBooked) {
      status = 'Booked';
    } else if (isCancelled) {
      status = 'Cancelled';
    } else if (isCompleted) {
      status = 'Completed';
    } else if (isBooked) {
      status = 'Booked';
    }

    this.editLeadForm.patchValue({ latest_inspection_status: status }, { emitEvent: false });
  }

  /** Map stored `channel` (id or legacy label) to the menu label for display. */
  commissionChannelDisplayLabel(channel: string | undefined | null): string {
    if (channel == null || channel === '') {
      return '';
    }
    const byId = this.contactAttemptOptions.find(o => o.id === channel);
    return byId?.label ?? channel;
  }

  commissionChannelPrimeIcon(channel: string | undefined | null): string {
    if (channel == null || channel === '') {
      return 'pi-phone';
    }
    const trimmed = channel.trim();
    const opt = this.contactAttemptOptions.find(o => o.id === trimmed || o.label === trimmed || o.id.toLowerCase() === trimmed.toLowerCase());
    if (opt?.icon) {
      return opt.icon;
    }
    const ch = trimmed.toLowerCase();
    if (ch === 'phone call' || ch.includes('phone call')) {
      return 'pi-phone';
    }
    if (ch === 'email' || (ch.includes('email') && !ch.includes('called'))) {
      return 'pi-envelope';
    }
    if (ch === 'sms' || ch.includes('sms')) {
      return 'pi-comment';
    }
    if (ch.includes('voicemail') || ch.includes('voice_mail')) {
      return 'pi-volume-up';
    }
    if (ch.includes('not connected') || ch.includes('not_connected')) {
      return 'pi-ban';
    }
    if (ch.includes('no answer') || ch.includes('no_answer')) {
      return 'pi-times-circle';
    }
    if (ch.includes('Contact Attempted')) {
      return 'pi-phone';
    }
    if (ch.includes('Call Answered')) {
      return 'pi-phone';
    }
    if (ch.includes('called')) {
      return 'pi-phone';
    }
    return 'pi-phone';
  }

  additional_notes = null;
  note_type = null;

  toggleCommissionType(type: string) {
    if (!this.commissionType) {
      this.commissionType = [];
    }

    const index = this.commissionType.indexOf(type);
    this.note_type = type;
    let f = this.commissionType?.find(f => f == type);
    if (!f && type !== 'Called - Call Answered') {
      this.commissionTypeAdded = [];
      this.commissionTypeAdded.push(type);

      let leadStatus = this.editLeadForm.get('lead_status')?.value;

      if (leadStatus == 'New') {
        leadStatus = 'Contact Attempted';
      }

      this.addCommission(leadStatus);
    }

    if (index > -1) {
      // ❌ remove if already exists
      this.commissionType.splice(index, 1);
    } else {
      // ✅ add if not exists
      this.commissionType.push(type);
    }

    let isExits = this.commissionType?.find(f => f == 'Called - Call Answered');

    let name = this.AuthUser?.actual_user
      ? this.AuthUser?.actual_user?.full_name
      : this.lead_detail?.agent_responsible_name
        ? this.lead_detail.agent_responsible_name
        : this.AuthUser?.full_name;

    const initials = name
      .split(' ')
      .filter((word: string) => word) // empty spaces remove
      .slice(0, 2) // sirf first 2 words
      .map((word: string) => word.charAt(0))
      .join('');

    this.additional_notes = isExits ? `${moment().format('DD/MM/YY')}: ${initials} - ` : null;

    if (isExits) {
      this.captureContactNoteBaseline();
    } else if (this.contactNoteBaseline !== null) {
      // Note box switched off — take the draft back out of report_notes.
      this.mergeContactNoteDraft();
      this.contactNoteBaseline = null;
    }
  }

  // ---------- Contact-attempt note draft ----------

  /**
   * report_notes as it stood before the contact-attempt note popup was opened. The live draft is
   * always rebuilt on top of this, so re-typing replaces the draft line instead of stacking copies.
   */
  private contactNoteBaseline: string | null = null;
  private readonly contactNoteSaveTick$ = new Subject<void>();

  private captureContactNoteBaseline(): void {
    if (this.contactNoteBaseline === null) {
      this.contactNoteBaseline = (this.editLeadForm.controls['report_notes'].value || '').trim();
    }
  }

  /** True once the draft holds real text beyond the auto-inserted "… initials - " stub. */
  private hasContactNoteContent(): boolean {
    const note = (this.additional_notes ?? '').trim();
    return !!note && !note.endsWith('-');
  }

  /** Rebuilds report_notes as draft + baseline and returns the merged text. */
  private mergeContactNoteDraft(): string {
    this.captureContactNoteBaseline();
    const baseline = this.contactNoteBaseline ?? '';
    const note = this.hasContactNoteContent() ? (this.additional_notes ?? '').trim() : '';
    const merged = note ? (baseline ? `${note}\n${baseline}` : note) : baseline;
    this.editLeadForm.patchValue({ report_notes: merged }, { emitEvent: false });
    this.syncLeadDetailReportNotesFromForm();
    return merged;
  }

  /**
   * Contact-attempt note keystroke: fold the draft into report_notes and queue a save, so the note
   * survives dismissing the popup. Runs in view mode too, hence its own tick rather than
   * scheduleAutoSave() (which only fires inside an edit session).
   */
  onContactNoteChange(): void {
    this.mergeContactNoteDraft();
    this.contactNoteSaveTick$.next();
  }

  /** Persist the draft immediately (popup dismissed) rather than waiting out the debounce. */
  private flushContactNoteDraft(): void {
    const hadContent = this.hasContactNoteContent();
    this.mergeContactNoteDraft();
    this.resetContactNoteDraft();
    if (hadContent) {
      this.saveContactNoteDraft();
    }
  }

  private resetContactNoteDraft(): void {
    this.additional_notes = null;
    this.contactNoteBaseline = null;
  }

  /** Shared guard for the note's own save path — no edit-mode requirement, unlike canAutoSave(). */
  private saveContactNoteDraft(): void {
    if (!this.isLeadDetailLoaded || !this.lead_detail?._id || !this.isAllowEdit()) {
      return;
    }
    const snapshot = this.snapshotForm();
    if (snapshot !== null && snapshot === this.lastSavedSnapshot) {
      return;
    }
    this.requestSilentSave();
  }

  hideNote() {
    this.commissionType = this.commissionType?.filter(f => f != 'Called - Call Answered');
    // The draft is already inside report_notes (onContactNoteChange); rebuild from the baseline
    // so Save & Close doesn't prepend the same line a second time.
    const updatedNotes = this.mergeContactNoteDraft();
    this.reportNotesSummaryComponent?.autoUpdateSummary(updatedNotes, false);
    this.resetContactNoteDraft();

    this.commissionTypeAdded = [];
    let leadStatus = this.editLeadForm.get('lead_status')?.value;
    if (leadStatus == 'New') {
      leadStatus = 'Nurturing (Pre Appointment)';
    }
    this.commissionTypeAdded.push('Called - Call Answered');
    this.addCommission(leadStatus);
  }

  getInitials(fullName: string): string {
    if (!fullName) return '';

    return fullName
      .trim()
      .split(' ')
      .filter(name => name.length > 0)
      .map(name => name[0].toUpperCase())
      .join('');
  }

  /** Initials for report note lines (e.g. "Nauman Op" → "NO"). */
  agentInitialsForNotes(): string {
    const u = this.AuthUser?.actual_user ? this.AuthUser?.actual_user : this.AuthUser;
    const full = typeof u?.full_name === 'string' ? u.full_name.trim() : '';
    if (full) {
      const initials = this.getInitials(full);
      if (initials) return initials;
    }
    const first = typeof u?.first_name === 'string' ? u.first_name.trim() : '';
    const last = typeof u?.last_name === 'string' ? u.last_name.trim() : '';
    const fi = first ? first[0].toUpperCase() : '';
    const li = last ? last[0].toUpperCase() : '';
    const fromParts = `${fi}${li}`;
    return fromParts || 'A';
  }

  updateReportNotes() {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const date = `${day}/${month}/${year}`;
    const existingNotes = (this.editLeadForm.controls['report_notes'].value || '').trim();

    const reporterName = this.agentInitialsForNotes();

    const notes = this.commissionTypeAdded
      .filter(id => id !== 'Called - Call Answered') // 👈 skip these types
      .map(id => {
        const opt = this.contactAttemptOptions.find(o => o.id === id);
        const text = opt?.label ?? String(id ?? '');

        return `${date}: ${reporterName} ${text}`;
      })
      .join('\n');

    // notes empty (e.g. only 'Called - Call Answered' selected, which is filtered out):
    // don't prepend a stray '\n' — that accumulates into blank lines ('\n\n').
    const updatedNotes = notes ? (existingNotes ? `${notes}\n${existingNotes}` : notes) : existingNotes;

    this.editLeadForm.patchValue({ report_notes: updatedNotes }, { emitEvent: false });
    this.reportNotesSummaryComponent?.autoUpdateSummary(updatedNotes, false);
  }
  /** Contact-attempt menu: `id` is sent to the API; `label` / `icon` for UI and history. */
  readonly contactAttemptOptions: ReadonlyArray<{ id: string; label: string; icon: string }> = [
    { id: 'Called - Call Answered', label: 'Called - Call Answered', icon: 'pi-phone' },
    { id: 'Called - Voicemail Left', label: 'Called - Voicemail Left', icon: 'pi-volume-up' },
    { id: 'Called - Not Accepted', label: 'Called - Not Accepted', icon: 'pi-times-circle' },
    { id: 'Called - No Voicemail', label: 'Called - No Voicemail', icon: 'pi-times-circle' },
    { id: 'Called - Not Connected', label: 'Called - Not Connected', icon: 'pi-ban' },
    { id: 'No Number Provided', label: 'No Number Provided', icon: 'pi-comment' },
    { id: 'SMS Sent', label: 'SMS Sent', icon: 'pi-comment' },
    { id: 'Email Sent', label: 'Email Sent', icon: 'pi-envelope' },
  ];
}
